import {
  EditOutlined,
  LockFilled,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { DatePicker, Row, Skeleton, Space, Typography } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ReturnsPopover } from 'components/Custom/ReturnsPopover';
import { ColoredTag } from 'components/Custom/spans';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { MessageStates } from 'containers/MessageBox';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import dayjs from 'dayjs';
import moment from 'moment';
import { formatFromNow } from 'i18n/momentLocale';
import { AccountTypeTag } from 'components/Custom/AccountTypeTag';
import * as React from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHistoryBlock } from 'hooks/useHistoryBlock';
import {
  AccountType,
  SubscriptionPlan,
  UpgradeMessageSource,
} from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import { DataLogStatus, DrawerType, WindowType } from 'store/app/enums';
import { getMenuSize } from 'store/app/selectors';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { User } from 'store/auth/models';
import { getAuthUserLicence } from 'store/auth/selectors';
import {
  getBillingLoading,
  getCurrentSubscription,
} from 'store/billing/selectors';
import { Organisation } from 'store/organisation/models';
import { MappedReturn } from 'store/returns/models/returnData';
import { VatReturnLog } from 'store/vat/models/vatReturnLog';
import styled from 'styled-components';
import { calculateNextDate } from 'utils/calculate-next-date';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
  setPreferencesInSection,
  getStoredPreferenceDetails,
  usePreferences,
} from 'utils/preferences';
import { StoreState } from 'store/types';
import { renderAvatar } from 'utils/render-avatar';
import { getCanonicalDashboardCardOrder } from 'utils/migrate-dashboard-card-ids';
import {
  filterPwaDashboardCards,
  getPwaMobileDashboardLayouts,
  PWA_DASHBOARD_BREAKPOINT_COLS,
} from 'utils/pwa-dashboard-layout';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { hasUnlimitedLicense } from 'utils/subscriptionPlanGate';
import { BaseCard } from './cards/BaseCard';
import {
  CARD_REGISTRY,
  getDefaultCardOrderForView,
  getDefaultDashboardCardOrderForView,
} from './cards/cardRegistry';
import { CardRenderer } from './cards/CardRenderer';
import { CardId } from './cards/types';
import { CardSelector } from './CardSelector';
import {
  DocumentCaptureBanner,
  DOCUMENT_CAPTURE_BANNER_HIDE,
} from './DocumentCaptureBanner';
import { PwaDashboardGrid } from './PwaDashboardGrid';
import { fetchViesInfoRequest } from 'store/vat-checker/actions';
import { getViesInfos, getViesLoading } from 'store/vat-checker/selectors';

const ResponsiveGridLayout = WidthProvider(Responsive);

const PINNED_INFO_ROW_CARD_IDS: CardId[] = [
  'tasksByStatus',
  'returnsByDueDate',
  'calendar',
];

const usesPinnedDashboardRowLayout = (cardId: CardId): boolean => {
  const category = CARD_REGISTRY[cardId]?.category;
  return category === 'summary' || category === 'capture';
};

const isPinnedInfoRowCard = (cardId: CardId): boolean =>
  PINNED_INFO_ROW_CARD_IDS.includes(cardId);

const usesPinnedLayout = (cardId: CardId): boolean =>
  usesPinnedDashboardRowLayout(cardId) || isPinnedInfoRowCard(cardId);

// Two pinned rows (summary + capture), each defaultHeight 2
const PINNED_DASHBOARD_ROWS_BOTTOM_Y = 4;

const savedPositionOverlapsPinnedRows = (savedY: number): boolean =>
  savedY >= 0 && savedY < PINNED_DASHBOARD_ROWS_BOTTOM_Y;

const { RangePicker } = DatePicker;
const dateFormat = 'DD/MM/YYYY';
const { Text } = Typography;

// Styled component for tabs container - matches submissions page format
const ViewTabsContainer = styled.div`
  flex: 1;

  .ant-tabs {
    margin-bottom: 0;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-ink-bar,
  .ant-tabs-ink-bar-animated {
    background: var(--color-primary, #41aaaf) !important;
    background-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-tabs-top > .ant-tabs-nav::before,
  .ant-tabs-nav::before {
    border-bottom: 1px solid #f0f0f0 !important;
  }

  .ant-tabs-content-holder {
    margin-top: 35px;
  }

  .ant-tabs-content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .ant-tabs-tabpane {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
`;

interface HorizontalBarProps {
  label: string;
  value: number;
  maxValue: number;
  onClick?: () => void;
  returns?: MappedReturn[];
  createSubmission?: (connectionId: string, mappingId: string) => void;
}

const HorizontalBar: React.FC<HorizontalBarProps> = ({
  label,
  value,
  maxValue,
  onClick,
  returns,
  createSubmission,
}) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  const barContent = (
    <div style={{ marginBottom: '16px', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <Text
          style={{ fontSize: '14px', color: 'var(--color-text-dark, #042a2a)' }}
        >
          {label}
        </Text>
        <Text
          style={{
            fontSize: '14px',
            color: 'var(--color-text-dark, #042a2a)',
            fontWeight: 600,
          }}
        >
          {value}
        </Text>
      </div>
      <div
        style={{
          width: '100%',
          height: '24px',
          backgroundColor: 'var(--color-surface, #F3F7F8)',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          cursor: onClick && value > 0 ? 'pointer' : 'default',
        }}
        onClick={onClick && value > 0 ? onClick : undefined}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: '#41aaaf',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'width 0.3s ease',
          }}
        >
          {value > 0 && (
            <Text
              style={{ color: '#ffffff', fontSize: '12px', fontWeight: 600 }}
            >
              {value}
            </Text>
          )}
        </div>
      </div>
    </div>
  );

  if (returns && returns.length > 0) {
    return (
      <ReturnsPopover
        returns={returns}
        onClick={onClick}
        onCreateSubmission={createSubmission}
      >
        {barContent}
      </ReturnsPopover>
    );
  }

  return barContent;
};

export interface HomeComponentProps {
  entities?: Organisation[];
  mappedReturns?: MappedReturn[];
  currentUser?: User;
  accountUsers: any[];
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  isLoading?: boolean;

  // View props
  dashboardViewType: 'manager' | 'my';
  setDashboardViewType: (value: 'manager' | 'my') => void;
  isManager: boolean;
  cardOrder: string[];
  saveCardOrder: (order: string[]) => void;
  visibleCards: string[];
  addCard: (cardId: string) => void;
  removeCard: (cardId: string) => void;
  saveCardVisibilityBatch: (
    visibleCards: string[],
    currentLayouts?: {
      xl?: any[];
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    }
  ) => void;
  resetVisibleCardsToDefault?: () => void;
  cardHeights: Record<string, number> | null;
  saveCardHeights: (heights: Record<string, number>) => void;
  cardWidths: Record<string, number> | null;
  saveCardWidths: (widths: Record<string, number>) => void;
  cardLayouts: {
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  } | null;
  saveCardLayouts: (layouts: {
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  }) => void;
  // State props (kept for backward compatibility, but will be replaced by dashboardViewType)
  showOnlyMyReturns: boolean;
  setShowOnlyMyReturns: (value: boolean) => void;
  submittedDateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  submittedDateRangeOption: string | any[];
  enableCustomRange: boolean;
  submittedReturnsData: any[];
  submittedDateFilterOpen: boolean;
  filteredSubmittedReturns: MappedReturn[];
  filterKey: string;
  selectedUserNames: string[];
  tempSelectedUserNames: string[];
  teamAllocationFilterOpen: boolean;
  hoveredTeamAllocation: boolean;
  setHoveredTeamAllocation: (value: boolean) => void;
  submittedThisMonth: number;
  captureDashboardStats?: import('store/organisation/models').ReceiptStatisticsTotals;

  // Computed values
  notStartedCount: number;
  pendingTasks: number;
  awaitingReview: number;
  savedTasks: number;
  awaitingClientTasks: number;
  reviewerApprovedTasks: number;
  awaitingApprovalTasks: number;
  approvedTasks: number;
  clientApprovedTasks: number;
  lockedTasks: number;
  submittedTasks: number;
  cancelledTasks: number;
  notSavedTasks: number;
  exceptionsModifiedTasks: number;
  notStartedTasks: number;
  overdueReturns: number;
  dueTodayReturns: number;
  dueThisWeekReturns: number;
  dueThisMonthReturns: number;
  dueNextMonthReturns: number;
  dueLaterReturns: number;
  preparerCount: number;
  reviewerCount: number;
  maxRoleValue: number;
  returnTypeCounts: { [key: string]: number };
  maxReturnTypeValue: number;
  userData: Array<{
    name: string;
    preparerCount: number;
    reviewerCount: number;
    totalCount: number;
  }>;
  filteredUserData: Array<{
    name: string;
    preparerCount: number;
    reviewerCount: number;
    totalCount: number;
  }>;

  // Handlers
  handleStatusClick: (status: DataLogStatus) => void;
  handleDueDateClick: (dueDateType: string) => void;
  handleReturnTypeClick: (returnTypeName: string) => void;
  handleRoleClick: (role: 'preparer' | 'reviewer') => void;
  handlePersonClick: (
    personName: string,
    role: 'preparer' | 'reviewer'
  ) => void;
  navigateToSubmissions: (filterType: string, filterValue?: string) => void;
  openSubmission: (
    connectionId: string,
    mappingId: string,
    dataLogId: string
  ) => void;
  createSubmission: (connectionId: string, mappingId: string) => void;
  onSubmittedDateOptionSelect: (value: string) => void;
  onCustomRangeCheckboxChange: (e: any) => void;
  handleApplyDateFilter: () => void;
  handleCancelDateFilter: () => void;
  handleOpenDateFilterDropdown: (open: boolean) => void;
  setSubmittedDateRange: (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => void;
  setSelectedUserNames: (value: string[]) => void;
  setTempSelectedUserNames: (value: string[]) => void;
  setTeamAllocationFilterOpen: (value: boolean) => void;
  TEAM_ALLOCATION_USER_FILTER: string;
  capturedSubmissionsPageSize: number;
  saveCapturedSubmissionsPageSize: (pageSize: number) => void;
  handleTotalEntitiesClick: () => void;
  handleTotalReturnsClick: () => void;
  handleNotStartedClick: () => void;
  handlePendingTasksClick: () => void;
  handleAwaitingReviewClick: () => void;
  handleSubmittedThisMonthClick: () => void;
  // Time to Submit props
  timeToSubmitDateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  timeToSubmitDateRangeOption: string | any[];
  enableTimeToSubmitCustomRange: boolean;
  timeToSubmitDateFilterOpen: boolean;
  timeToSubmitMetrics: Array<{
    name: string;
    avgDaysToSubmit: number;
    totalSubmissions: number;
    fastestSubmission: number | null;
    slowestSubmission: number | null;
    submissionsThisMonth: number;
    submissionsThisWeek: number;
  }>;
  onTimeToSubmitDateOptionSelect: (value: string) => void;
  onTimeToSubmitCustomRangeCheckboxChange: (e: any) => void;
  handleApplyTimeToSubmitDateFilter: () => void;
  handleCancelTimeToSubmitDateFilter: () => void;
  handleOpenTimeToSubmitDateFilterDropdown: (open: boolean) => void;
  setTimeToSubmitDateRange: (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => void;
}

export const HomeComponent: React.FC<HomeComponentProps> = ({
  entities,
  mappedReturns,
  currentUser,
  handlePreferences,
  isLoading = false,
  dashboardViewType,
  setDashboardViewType,
  isManager,
  cardOrder,
  saveCardOrder,
  visibleCards,
  addCard,
  removeCard,
  saveCardVisibilityBatch,
  resetVisibleCardsToDefault,
  cardHeights,
  saveCardHeights,
  cardWidths,
  saveCardWidths,
  cardLayouts,
  saveCardLayouts,
  showOnlyMyReturns,
  setShowOnlyMyReturns,
  submittedDateRange,
  submittedDateRangeOption,
  enableCustomRange,
  submittedReturnsData,
  submittedDateFilterOpen,
  filteredSubmittedReturns,
  filterKey,
  selectedUserNames,
  tempSelectedUserNames,
  teamAllocationFilterOpen,
  hoveredTeamAllocation,
  setHoveredTeamAllocation,
  submittedThisMonth,
  captureDashboardStats,
  notStartedCount,
  pendingTasks,
  awaitingReview,
  savedTasks,
  awaitingClientTasks,
  reviewerApprovedTasks,
  awaitingApprovalTasks,
  approvedTasks,
  clientApprovedTasks,
  lockedTasks,
  submittedTasks,
  cancelledTasks,
  notSavedTasks,
  exceptionsModifiedTasks,
  notStartedTasks,
  overdueReturns,
  dueTodayReturns,
  dueThisWeekReturns,
  dueThisMonthReturns,
  dueNextMonthReturns,
  dueLaterReturns,
  preparerCount,
  reviewerCount,
  maxRoleValue,
  returnTypeCounts,
  maxReturnTypeValue,
  filteredUserData,
  accountUsers,
  userData,
  handleStatusClick,
  handleDueDateClick,
  handleReturnTypeClick,
  handleRoleClick,
  handlePersonClick,
  navigateToSubmissions,
  openSubmission,
  createSubmission,
  onSubmittedDateOptionSelect,
  onCustomRangeCheckboxChange,
  handleApplyDateFilter,
  handleCancelDateFilter,
  handleOpenDateFilterDropdown,
  setSubmittedDateRange,
  setSelectedUserNames,
  setTempSelectedUserNames,
  setTeamAllocationFilterOpen,
  TEAM_ALLOCATION_USER_FILTER,
  capturedSubmissionsPageSize,
  saveCapturedSubmissionsPageSize,
  handleTotalEntitiesClick,
  handleTotalReturnsClick,
  handleNotStartedClick,
  handlePendingTasksClick,
  handleAwaitingReviewClick,
  handleSubmittedThisMonthClick,
  timeToSubmitDateRange,
  timeToSubmitDateRangeOption,
  enableTimeToSubmitCustomRange,
  timeToSubmitDateFilterOpen,
  timeToSubmitMetrics,
  onTimeToSubmitDateOptionSelect,
  onTimeToSubmitCustomRangeCheckboxChange,
  handleApplyTimeToSubmitDateFilter,
  handleCancelTimeToSubmitDateFilter,
  handleOpenTimeToSubmitDateFilterDropdown,
  setTimeToSubmitDateRange,
}) => {
  const { t, i18n } = useTranslation();
  const menuSize = useSelector(getMenuSize);
  const currentSubscription = useSelector(getCurrentSubscription);
  const billingLoading = useSelector(getBillingLoading);
  const pwaMobileNav = usePwaMobileNavEnabled();
  const userLicence = useSelector(getAuthUserLicence);

  // Check if user has access to Manager View and Edit functionality
  // Access is available for Medium, Large, and Enterprise plans, and unlimited licenses
  const hasManagerViewAccess = React.useMemo(() => {
    if (hasUnlimitedLicense(userLicence)) return true;
    // If subscription is still loading, don't show lock (return true to avoid showing lock during load)
    if (billingLoading || !currentSubscription) return true;
    // If subscription exists but no description, don't show lock (assume still loading)
    if (!currentSubscription.description) return true;
    const plan = currentSubscription.description;
    return (
      plan === SubscriptionPlan.MEDIUM_PLAN ||
      plan === SubscriptionPlan.LARGE_PLAN ||
      plan === SubscriptionPlan.ENTERPRISE_PLAN
    );
  }, [currentSubscription, billingLoading, userLicence]);

  // Track window size for responsive grid (debounced)
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // Calendar month navigation state
  const [calendarValue, setCalendarValue] = React.useState(dayjs());

  // Calendar mode from user preferences (backend), default month
  const prefs = usePreferences();
  const calendarMode =
    (prefs(
      PreferenceActions.GET,
      PreferenceSections.APP,
      { name: 'calendarMode' },
      currentUser?.userUuid ?? ''
    ) as 'month' | 'year' | false) || 'month';
  const saveCalendarMode = React.useCallback(
    (mode: 'month' | 'year') => {
      prefs(
        PreferenceActions.SET,
        PreferenceSections.APP,
        { name: 'calendarMode', value: mode },
        currentUser?.userUuid ?? ''
      );
    },
    [prefs, currentUser?.userUuid]
  );

  // Helper function to find user from accountUsers by name
  const findUserByName = React.useCallback(
    (name: string): User | undefined => {
      if (!accountUsers || accountUsers.length === 0) return undefined;
      return accountUsers.find((user) => {
        const fullName = `${user.firstName || ''} ${
          user.lastName || ''
        }`.trim();
        return fullName === name || user.email === name;
      });
    },
    [accountUsers]
  );

  // Helper function to render Role
  const renderRole = React.useCallback(
    (_content: string, record: any) => {
      const user = findUserByName(record.name);
      return <AccountTypeTag accountTypeKey={user?.accountType} t={t} />;
    },
    [findUserByName, t]
  );

  // Helper function to render Last Login
  const renderLastLogin = React.useCallback(
    (_: string, record: any) => {
      const user = findUserByName(record.name);
      if (!user) {
        return (
          <Text
            style={{
              fontSize: '14px',
              color: 'var(--color-text-dark, #042a2a)',
            }}
          >
            -
          </Text>
        );
      }

      if (user.accountActivated && user.lastLogin) {
        const value = formatFromNow(user.lastLogin, i18n.language);
        return (
          <Text
            style={{
              fontSize: '14px',
              color: 'var(--color-text-dark, #042a2a)',
            }}
          >
            {value}
          </Text>
        );
      } else {
        return (
          <Text
            style={{
              fontSize: '14px',
              color: 'var(--color-text-dark, #042a2a)',
            }}
          >
            {t('common.never')}
          </Text>
        );
      }
    },
    [findUserByName, t, i18n.language]
  );

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150); // Debounce resize events by 150ms
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Helper function to filter returns that should be included based on view type
  const shouldIncludeReturn = (item: MappedReturn) => {
    // In Manager View, show all returns
    if (dashboardViewType === 'manager') return true;
    // In My View, only show returns where user is assigned
    return (
      item.dataLog?.preparerUser?.userUuid === currentUser?.userUuid ||
      item.dataLog?.reviewerUser?.userUuid === currentUser?.userUuid ||
      item.dataLog?.clientUser?.userUuid === currentUser?.userUuid
    );
  };

  // Filter returns for Total Returns card
  const getAllReturns = React.useMemo(() => {
    return mappedReturns?.filter((item) => shouldIncludeReturn(item)) || [];
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Filter returns for Not Started card
  const getNotStartedReturns = React.useMemo(() => {
    return (
      mappedReturns?.filter(
        (item) =>
          (!item.status || item.status === '') && shouldIncludeReturn(item)
      ) || []
    );
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Filter returns for Pending Tasks card
  const getPendingTasksReturns = React.useMemo(() => {
    return (
      mappedReturns?.filter(
        (item) =>
          item.dataLog &&
          item.status !== DataLogStatus.SUBMITTED &&
          item.status !== DataLogStatus.CANCELLED &&
          item.status !== DataLogStatus.LOCKED &&
          shouldIncludeReturn(item)
      ) || []
    );
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Filter returns for Awaiting Review card (Awaiting Approval and Awaiting Client Approval)
  const getAwaitingReviewReturns = React.useMemo(() => {
    return (
      mappedReturns?.filter(
        (item) =>
          item.dataLog &&
          (item.status === DataLogStatus.AWAITING_APPROVAL ||
            item.status === DataLogStatus.AWAITING_CLIENT_APPROVAL) &&
          shouldIncludeReturn(item)
      ) || []
    );
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Filter returns for Submitted this month card
  const getSubmittedThisMonthReturns = React.useMemo(() => {
    const today = dayjs();
    const startDate = today.startOf('month');
    const endDate = today.startOf('month').add(1, 'month');

    const allSubmittedLogs: VatReturnLog[] = [];
    const seenLogs = new Set<string>();

    if (mappedReturns && mappedReturns.length > 0) {
      mappedReturns.forEach((ret) => {
        if (
          ret &&
          ret.dataLog &&
          ret.dataLog.submissionDate &&
          ret.dataLog.status
        ) {
          try {
            if (
              DataLogStatus[ret.dataLog.status] === DataLogStatus.SUBMITTED &&
              shouldIncludeReturn(ret)
            ) {
              const logKey = `${ret.dataLog.uuid}-${ret.mappingId}-${ret.dataLog.submissionDate}`;
              if (!seenLogs.has(logKey)) {
                seenLogs.add(logKey);
                allSubmittedLogs.push(ret.dataLog);
              }
            }
          } catch (e) {
            // Silently handle errors
          }
        }
      });
    }

    const thisMonthLogs = allSubmittedLogs.filter((log: VatReturnLog) => {
      if (!log || !log.submissionDate) return false;
      try {
        const submissionDate = dayjs(log.submissionDate);
        if (!submissionDate.isValid()) {
          return false;
        }
        const subDate = submissionDate.startOf('day');
        const start = startDate.startOf('day');
        const end = endDate.startOf('day');

        const subTimestamp = subDate.valueOf();
        const startTimestamp = start.valueOf();
        const endTimestamp = end.valueOf();
        const isInRange =
          subTimestamp >= startTimestamp && subTimestamp < endTimestamp;

        return isInRange;
      } catch (e) {
        return false;
      }
    });

    // Map logs back to returns
    const returnMap = new Map<string, MappedReturn>();
    mappedReturns?.forEach((ret) => {
      if (ret.dataLog && ret.mappingId) {
        const key = `${ret.dataLog.uuid}-${ret.mappingId}`;
        returnMap.set(key, ret);
      }
    });

    return thisMonthLogs
      .map((log) => {
        const key = `${log.uuid}-${log.mappingId || ''}`;
        return returnMap.get(key);
      })
      .filter(
        (ret): ret is MappedReturn =>
          ret !== undefined && shouldIncludeReturn(ret)
      );
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Filter returns by status for Tasks by Status pie chart
  const getReturnsByStatus = React.useCallback(
    (status: DataLogStatus | string) => {
      if (
        status === '' ||
        status === 'NOT_STARTED' ||
        status === DataLogStatus.NOT_STARTED
      ) {
        return (
          mappedReturns?.filter(
            (item) =>
              shouldIncludeReturn(item) &&
              (!item.status ||
                item.status === '' ||
                item.status === DataLogStatus.NOT_STARTED)
          ) || []
        );
      }
      return (
        mappedReturns?.filter(
          (item) => item.status === status && shouldIncludeReturn(item)
        ) || []
      );
    },
    [mappedReturns, dashboardViewType, currentUser]
  );

  // Filter returns by due date category
  const getReturnsByDueDate = React.useCallback(
    (dueDateType: string) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      const monthFromNow = new Date(today);
      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
      const nextMonthFromNow = new Date(today);
      nextMonthFromNow.setMonth(nextMonthFromNow.getMonth() + 2);

      return (
        mappedReturns?.filter((item) => {
          if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
          // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
          if (
            item.status === DataLogStatus.SUBMITTED ||
            item.status === DataLogStatus.CANCELLED ||
            item.status === DataLogStatus.NOT_STARTED ||
            !item.status ||
            item.status === ''
          ) {
            return false;
          }
          // Only include the same open statuses that the filter includes
          const allowedStatuses = [
            DataLogStatus.LOCKED,
            DataLogStatus.SAVED,
            DataLogStatus.AWAITING_APPROVAL,
            DataLogStatus.INTERNAL_APPROVED,
            DataLogStatus.APPROVED,
            DataLogStatus.AWAITING_CLIENT_APPROVAL,
            DataLogStatus.CLIENT_APPROVED,
          ];
          if (!allowedStatuses.includes(item.status as DataLogStatus))
            return false;
          const dueDate = new Date(item.nextDueDate);

          switch (dueDateType) {
            case 'overdue':
              return dueDate < today;
            case 'dueToday':
              return (
                dueDate.getDate() === today.getDate() &&
                dueDate.getMonth() === today.getMonth() &&
                dueDate.getFullYear() === today.getFullYear()
              );
            case 'dueThisWeek':
              return dueDate > today && dueDate <= weekFromNow;
            case 'dueThisMonth':
              return dueDate > weekFromNow && dueDate <= monthFromNow;
            case 'dueNextMonth':
              return dueDate > monthFromNow && dueDate <= nextMonthFromNow;
            case 'dueLater':
              return dueDate > nextMonthFromNow;
            default:
              return false;
          }
        }) || []
      );
    },
    [mappedReturns, dashboardViewType, currentUser]
  );

  // Filter returns by return type - typeName is "CountryName - Return Name" (matches submissions mappingName)
  const getReturnsByType = React.useCallback(
    (typeName: string) => {
      return (
        mappedReturns?.filter((item) => {
          if (!item.returnTypeId || !shouldIncludeReturn(item)) return false;
          // Exclude submitted returns
          if (item.status === DataLogStatus.SUBMITTED) return false;
          let returnTypeName = t('common.unknown');
          let countryName = t('common.unknown');
          entities?.forEach((entity) => {
            const returnType = entity.returnTypesEdit?.find(
              (rt) => rt.id === item.returnTypeId
            );
            if (returnType) {
              returnTypeName = returnType.name || t('common.unknown');
              countryName = returnType.countryName || t('common.unknown');
            }
          });
          const key = `${countryName} - ${returnTypeName}`;
          return key === typeName;
        }) || []
      );
    },
    [mappedReturns, entities, dashboardViewType, currentUser]
  );

  // Filter returns by role (preparer/reviewer) - excluding submitted
  const getReturnsByRole = React.useCallback(
    (role: 'preparer' | 'reviewer') => {
      return (
        mappedReturns?.filter((item) => {
          if (!shouldIncludeReturn(item)) return false;
          // Exclude submitted returns
          if (item.status === DataLogStatus.SUBMITTED) return false;
          if (role === 'preparer') {
            return (
              item.dataLog?.preparerUser?.userUuid === currentUser?.userUuid
            );
          } else {
            return (
              item.dataLog?.reviewerUser?.userUuid === currentUser?.userUuid
            );
          }
        }) || []
      );
    },
    [mappedReturns, currentUser, dashboardViewType]
  );

  // Filter returns by person and role for Team Allocation - excluding submitted
  const getReturnsByPersonAndRole = React.useCallback(
    (personName: string, role: 'preparer' | 'reviewer') => {
      return (
        mappedReturns?.filter((item) => {
          if (!shouldIncludeReturn(item)) return false;
          // Exclude submitted returns
          if (item.status === DataLogStatus.SUBMITTED) return false;
          if (role === 'preparer' && item.dataLog?.preparerUser) {
            const preparerName =
              `${item.dataLog.preparerUser.firstName || ''} ${
                item.dataLog.preparerUser.lastName || ''
              }`.trim() ||
              item.dataLog.preparerUser.email ||
              t('common.unknown');
            return preparerName === personName;
          } else if (role === 'reviewer' && item.dataLog?.reviewerUser) {
            const reviewerName =
              `${item.dataLog.reviewerUser.firstName || ''} ${
                item.dataLog.reviewerUser.lastName || ''
              }`.trim() ||
              item.dataLog.reviewerUser.email ||
              t('common.unknown');
            return reviewerName === personName;
          }
          return false;
        }) || []
      );
    },
    [mappedReturns, dashboardViewType, currentUser]
  );

  // Calculate all upcoming due dates for calendar - excluding submitted
  const getUpcomingDueDates = React.useMemo(() => {
    const today = dayjs();
    const sixMonthsFromNow = today.add(6, 'months');
    const dueDatesMap: { [dateKey: string]: MappedReturn[] } = {};

    mappedReturns?.forEach((returnItem) => {
      if (!shouldIncludeReturn(returnItem)) return;
      // Exclude submitted returns
      if (returnItem.status === DataLogStatus.SUBMITTED) return;
      if (!returnItem.returnFrequency) return;

      // Get starting due date - try to parse nextDueDate
      let currentDueDate: dayjs.Dayjs | null = null;

      if (returnItem.nextDueDate) {
        try {
          const parsedDate = new Date(returnItem.nextDueDate);
          if (!isNaN(parsedDate.getTime())) {
            currentDueDate = dayjs(parsedDate);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      if (!currentDueDate || !currentDueDate.isValid()) return;

      // Generate all due dates for the next 6 months
      let checkDate = currentDueDate;
      let iterations = 0;
      const maxIterations = 50; // Safety limit

      while (
        checkDate.isBefore(sixMonthsFromNow) ||
        checkDate.isSame(sixMonthsFromNow, 'day')
      ) {
        if (checkDate.isAfter(today, 'day') || checkDate.isSame(today, 'day')) {
          const dateKey = checkDate.format('YYYY-MM-DD');
          if (!dueDatesMap[dateKey]) {
            dueDatesMap[dateKey] = [];
          }
          dueDatesMap[dateKey].push(returnItem);
        }

        // Calculate next due date
        const nextDate = calculateNextDate(
          checkDate.toDate(),
          returnItem.returnFrequency!
        );
        checkDate = dayjs(nextDate);

        iterations++;
        if (iterations >= maxIterations) break;
      }
    });

    return dueDatesMap;
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Get returns for a specific date
  const getReturnsForDate = React.useCallback(
    (date: dayjs.Dayjs) => {
      const dateKey = date.format('YYYY-MM-DD');
      return getUpcomingDueDates[dateKey] || [];
    },
    [getUpcomingDueDates]
  );

  // Define card configurations
  interface CardConfig {
    id: CardId;
    w: number;
    h: number;
    x: number;
    y: number;
    minW?: number;
    minH?: number;
    view?: 'manager' | 'my' | 'both';
  }

  // Default card order and layout configuration - view-specific
  // Use getDefaultCardOrderForView from cardRegistry to ensure consistency
  const getDefaultCardOrder = React.useCallback((): CardId[] => {
    return getDefaultCardOrderForView(dashboardViewType);
  }, [dashboardViewType]);

  const defaultCardOrder = getDefaultCardOrder();

  const dispatch = useDispatch();
  const store = useStore<StoreState>();
  const navigate = useNavigate();
  const location = useLocation();
  const allowNextNavigationRef = React.useRef(false);
  const pendingViewSwitchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Clear pending view-switch timeout on unmount to avoid state update after unmount
  React.useEffect(() => {
    return () => {
      if (pendingViewSwitchTimeoutRef.current) {
        clearTimeout(pendingViewSwitchTimeoutRef.current);
        pendingViewSwitchTimeoutRef.current = null;
      }
    };
  }, []);

  // Handler to open upgrade modal
  const handleUpgradeClick = React.useCallback(() => {
    const modalData: ModalData = {
      title: '',
      type: WindowType.WARNING,
      component: (
        <UpgradeMessage
          source={UpgradeMessageSource.MANAGER_VIEW}
          onClose={() => dispatch(turnModalOff())}
          userAccountType={currentUser?.accountType}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  }, [dispatch, currentUser]);

  // Calculate submitted this month directly from mappedReturns, independent of Total Returns Submitted filter
  // This queries the logged-in user's submitted returns this month directly (respects view type: manager vs my)
  const submittedThisMonthCalculated = React.useMemo(() => {
    if (!mappedReturns || mappedReturns.length === 0) {
      return 0;
    }

    const today = dayjs();
    const startDate = today.startOf('month');
    const endDate = today.startOf('month').add(1, 'month');

    // Filter returns that are submitted this month and belong to the current user (based on view type)
    const thisMonthReturns = mappedReturns.filter((ret) => {
      // Must include this return based on view type (manager vs my)
      if (!shouldIncludeReturn(ret)) {
        return false;
      }

      // Must be submitted
      if (!ret.dataLog || !ret.dataLog.status) {
        return false;
      }

      // Check if status is SUBMITTED (handle both string and enum)
      const status = DataLogStatus[ret.dataLog.status];
      if (status !== DataLogStatus.SUBMITTED) {
        return false;
      }

      // Must have a submission date
      if (!ret.dataLog.submissionDate) {
        return false;
      }

      // Must be within this month
      try {
        const submissionDate = dayjs(ret.dataLog.submissionDate);
        if (!submissionDate.isValid()) {
          return false;
        }
        const subDate = submissionDate.startOf('day');
        const start = startDate.startOf('day');
        const end = endDate.startOf('day');

        const subTimestamp = subDate.valueOf();
        const startTimestamp = start.valueOf();
        const endTimestamp = end.valueOf();

        return subTimestamp >= startTimestamp && subTimestamp < endTimestamp;
      } catch (e) {
        return false;
      }
    });

    return thisMonthReturns.length;
  }, [mappedReturns, dashboardViewType, currentUser]);

  // Use calculated value which queries data directly for the logged-in user
  const submittedThisMonthValue = submittedThisMonthCalculated;

  // Edit mode state - declared early so it can be used in getDefaultLayout
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [pendingCardOrder, setPendingCardOrder] = React.useState<CardId[]>([]);
  const [pendingReset, setPendingReset] = React.useState(false);
  const [justSavedReset, setJustSavedReset] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);
  // Ref to store the layout at the moment of save, to prevent GridLayout from changing it
  const savedLayoutsRef = React.useRef<{
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  } | null>(null);

  // Ref to store the current layout from GridLayout for use when removing cards
  // This ensures we can preserve positions even if pendingLayouts is empty
  const currentLayoutRef = React.useRef<{
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  } | null>(null);

  const [originalCardOrderBeforeReset, setOriginalCardOrderBeforeReset] =
    React.useState<CardId[]>([]);

  // Pending layout changes (not saved until Save is clicked)
  const [pendingLayouts, setPendingLayouts] = React.useState<{
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  }>({});
  const [pendingLayout, setPendingLayout] = React.useState<any[]>([]);
  const [pendingHeights, setPendingHeights] = React.useState<Record<
    string,
    number
  > | null>(null);
  const [pendingWidths, setPendingWidths] = React.useState<Record<
    string,
    number
  > | null>(null);

  // Original state before entering edit mode (for cancel)
  const [originalLayoutsBeforeEdit, setOriginalLayoutsBeforeEdit] =
    React.useState<{
      xl?: any[];
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    }>({});
  const [originalLayoutBeforeEdit, setOriginalLayoutBeforeEdit] =
    React.useState<any[]>([]);
  const [originalHeightsBeforeEdit, setOriginalHeightsBeforeEdit] =
    React.useState<Record<string, number> | null>(null);
  const [originalWidthsBeforeEdit, setOriginalWidthsBeforeEdit] =
    React.useState<Record<string, number> | null>(null);

  // Stateful layout positions - preserves actual positions for all breakpoints
  // Initialize from saved preferences if available
  const [savedLayouts, setSavedLayouts] = React.useState<{
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  }>(cardLayouts || {});

  // For backward compatibility, also maintain single layout state
  const [savedLayout, setSavedLayout] = React.useState<any[]>(
    cardLayouts?.xl || cardLayouts?.lg || []
  );

  // Update saved layouts when cardLayouts prop changes (from preferences)
  React.useEffect(() => {
    if (cardLayouts) {
      setSavedLayouts(cardLayouts);
      setSavedLayout(cardLayouts.xl || cardLayouts.lg || []);
    } else {
      setSavedLayouts({});
      setSavedLayout([]);
    }
  }, [cardLayouts]);

  // CRITICAL: Clear pending state when switching views to prevent data from previous view interfering
  // When switching from My View to Manager View (or vice versa), we must clear all pending state
  // This ensures that layouts, heights, widths, and card order from one view don't affect the other
  React.useEffect(() => {
    // Clear all pending state when view type changes
    // This ensures the new view starts fresh with its own saved preferences
    setPendingLayouts({});
    setPendingLayout([]);
    setPendingHeights(null);
    setPendingWidths(null);
    setPendingCardOrder([]);
    setPendingReset(false);
    setJustSaved(false);
    setJustSavedReset(false);
    // Clear refs as well
    savedLayoutsRef.current = null;
    currentLayoutRef.current = null;
    // Exit edit mode if we're in it (switching views should exit edit mode)
    if (isEditMode) {
      setIsEditMode(false);
    }
  }, [dashboardViewType]);

  // Define granular responsive breakpoints with flexible columns
  // Gradually reduces from 12 to 4 columns as screen size decreases
  const breakpoints = React.useMemo(
    () => ({
      xl: 1600, // Extra large: 12 columns
      lg: 1200, // Large: 12 columns
      md: 992, // Medium: 10 columns
      sm: 768, // Small: 8 columns
      xs: 576, // Extra small: 6 columns
      xxs: 0, // Extra extra small: 4 columns
    }),
    []
  );

  const cols = React.useMemo(
    () =>
      pwaMobileNav
        ? { ...PWA_DASHBOARD_BREAKPOINT_COLS }
        : {
            xl: 12,
            lg: 12,
            md: 10,
            sm: 8,
            xs: 6,
            xxs: 4,
          },
    [pwaMobileNav]
  );

  // Generate layout for a specific breakpoint (xl/lg/md/sm/xs/xxs)
  const generateLayoutForBreakpoint = React.useCallback(
    (
      cards: CardId[],
      breakpoint: 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
    ): Array<{ i: string; x: number; y: number; w: number; h: number }> => {
      // Base card dimensions (for desktop/12-column grid)
      // Build baseLayouts from CARD_REGISTRY to ensure consistency
      // All cards use exact defaultWidth and defaultHeight from cardRegistry.ts
      const baseLayouts = Object.keys(CARD_REGISTRY).reduce((acc, cardId) => {
        const config = CARD_REGISTRY[cardId as CardId];
        acc[cardId as CardId] = {
          w: config.defaultWidth,
          h: config.defaultHeight,
        };
        return acc;
      }, {} as Record<CardId, { w: number; h: number }>);

      // Use saved heights and widths if available and reset is not pending
      // CRITICAL: After reset, cardHeights and cardWidths should be null/empty
      // If they're not null but empty objects, we should use defaults (baseLayouts)
      // Always start with cardRegistry.ts defaults as the source of truth
      const layouts: Record<CardId, { w: number; h: number }> = {
        ...baseLayouts,
      };

      // Only apply saved preferences if:
      // 1. We're not in a reset state
      // 2. Saved preferences exist and are not empty
      // 3. The saved value differs from the current default (to avoid overriding with stale defaults)
      if (!pendingReset && cardHeights && Object.keys(cardHeights).length > 0) {
        Object.keys(cardHeights).forEach((cardId) => {
          if (usesPinnedLayout(cardId as CardId)) {
            return;
          }
          const cardConfig = CARD_REGISTRY[cardId as CardId];
          const savedHeight = cardHeights[cardId];
          const defaultHeight = cardConfig?.defaultHeight;

          // Only apply saved height if it's valid and different from current default
          // This ensures cardRegistry.ts defaults always take precedence when they change
          if (
            cardId in layouts &&
            savedHeight > 0 &&
            savedHeight !== defaultHeight
          ) {
            layouts[cardId as CardId].h = savedHeight;
          }
        });
      }

      if (!pendingReset && cardWidths && Object.keys(cardWidths).length > 0) {
        Object.keys(cardWidths).forEach((cardId) => {
          if (usesPinnedLayout(cardId as CardId)) {
            return;
          }
          const cardConfig = CARD_REGISTRY[cardId as CardId];
          const savedWidth = cardWidths[cardId];
          const defaultWidth = cardConfig?.defaultWidth;

          // Only apply saved width if it's valid and different from current default
          // This ensures cardRegistry.ts defaults always take precedence when they change
          if (
            cardId in layouts &&
            savedWidth > 0 &&
            savedWidth !== defaultWidth
          ) {
            layouts[cardId as CardId].w = savedWidth;
          }
        });
      }

      // Get column count for this breakpoint
      const columnCount = cols[breakpoint];

      // Responsive width adjustments based on breakpoint
      // Gradually scales card widths to fit the available columns
      const getResponsiveWidth = (
        baseW: number,
        bp: 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
      ): number => {
        const columnCount = cols[bp];
        const scaleFactor = columnCount / 12; // Scale factor based on column reduction

        if (bp === 'xxs') {
          // Extra extra small (4 columns): Stack most cards vertically
          if (baseW === 12) return 4; // Full width cards use all 4 columns
          if (baseW >= 8) return 4; // Large cards become full width
          if (baseW >= 4) return 4; // Medium cards become full width
          return 2; // Small cards (2 columns) become half width (2 of 4)
        } else if (bp === 'xs') {
          // Extra small (6 columns): More flexibility
          if (baseW === 12) return 6; // Full width cards use all 6 columns
          if (baseW >= 8) return 6; // Large cards become full width
          if (baseW >= 4) return 3; // Medium cards become half width (3 of 6)
          return 2; // Small cards stay small (2 of 6)
        } else if (bp === 'sm') {
          // Small (8 columns): Balance between mobile and tablet
          if (baseW === 12) return 8; // Full width cards use all 8 columns
          if (baseW >= 8) return 8; // Large cards become full width
          if (baseW >= 4) return 4; // Medium cards become half width (4 of 8)
          return 2; // Small cards stay small (2 of 8)
        } else if (bp === 'md') {
          // Medium (10 columns): Slightly reduced from desktop
          if (baseW === 12) return 10; // Full width cards use all 10 columns
          if (baseW >= 8) return Math.min(Math.round(baseW * scaleFactor), 10); // Scale large cards
          if (baseW >= 4) return Math.min(Math.round(baseW * scaleFactor), 5); // Scale medium cards
          return baseW; // Small cards stay the same
        } else {
          // xl and lg (12 columns): Use original widths
          return baseW;
        }
      };

      let currentY = 0;
      let currentX = 0;
      let maxYInRow = 0;
      let previousCategory: string | undefined;
      let previousCardId: CardId | undefined;

      // Summary + capture KPIs first, then the info trio — never pack large cards above KPIs.
      const orderedCards = getCanonicalDashboardCardOrder(cards);

      return orderedCards.map((cardId) => {
        const baseLayout = layouts[cardId] || { w: 4, h: 4 };
        const cardConfig = CARD_REGISTRY[cardId];
        const cardCategory = cardConfig?.category;
        const responsiveW = getResponsiveWidth(baseLayout.w, breakpoint);
        const cardLayout = { w: responsiveW, h: baseLayout.h };
        const isInfoRowCard = isPinnedInfoRowCard(cardId);
        const previousWasInfoRowCard =
          previousCardId != null && isPinnedInfoRowCard(previousCardId);

        if (
          cardCategory === 'capture' &&
          previousCategory !== 'capture' &&
          currentX > 0
        ) {
          currentY += maxYInRow;
          currentX = 0;
          maxYInRow = 0;
        }

        if (cardCategory !== 'capture' && previousCategory === 'capture') {
          currentY += maxYInRow;
          currentX = 0;
          maxYInRow = 0;
        }

        if (isInfoRowCard && !previousWasInfoRowCard && currentX > 0) {
          currentY += maxYInRow;
          currentX = 0;
          maxYInRow = 0;
        }

        if (!isInfoRowCard && previousWasInfoRowCard) {
          currentY += maxYInRow;
          currentX = 0;
          maxYInRow = 0;
        }

        // Check if card fits in current row
        if (currentX + cardLayout.w > columnCount) {
          currentY += maxYInRow;
          currentX = 0;
          maxYInRow = 0;
        }

        // Use minWidth and minHeight from card registry, with fallback
        // CRITICAL: Ensure minW never exceeds w - this prevents prop validation errors
        // when responsive widths are smaller than the card's minWidth for smaller breakpoints
        const cardMinW =
          cardConfig?.minWidth ||
          (breakpoint === 'xxs' || breakpoint === 'xs' ? 2 : 2);
        const minW = Math.min(cardMinW, cardLayout.w); // Clamp minW to never exceed w
        const minH = cardConfig?.minHeight || 2;

        const layout = {
          i: cardId,
          x: currentX,
          y: currentY,
          w: cardLayout.w,
          h: cardLayout.h,
          minW: minW,
          minH: minH,
        };

        currentX += cardLayout.w;
        maxYInRow = Math.max(maxYInRow, cardLayout.h);
        previousCategory = cardCategory;
        previousCardId = cardId;

        return layout;
      });
    },
    [
      dashboardViewType,
      cardHeights,
      cardWidths,
      pendingReset,
      cols,
      isEditMode,
      pendingHeights,
      pendingWidths,
    ]
  );

  // Generate layouts for all breakpoints
  const getDefaultLayouts = React.useCallback(
    (cards: CardId[]) => {
      if (pwaMobileNav) {
        return getPwaMobileDashboardLayouts(cards);
      }
      return {
        xl: generateLayoutForBreakpoint(cards, 'xl'),
        lg: generateLayoutForBreakpoint(cards, 'lg'),
        md: generateLayoutForBreakpoint(cards, 'md'),
        sm: generateLayoutForBreakpoint(cards, 'sm'),
        xs: generateLayoutForBreakpoint(cards, 'xs'),
        xxs: generateLayoutForBreakpoint(cards, 'xxs'),
      };
    },
    [generateLayoutForBreakpoint, pwaMobileNav]
  );

  // Default layout positions (for backward compatibility - uses desktop layout)
  const getDefaultLayout = React.useCallback(
    (cards: CardId[]): CardConfig[] => {
      const lgLayout = generateLayoutForBreakpoint(cards, 'lg');
      return lgLayout.map((l) => ({
        id: l.i as CardId,
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
        minW: (l as any).minW,
        minH: (l as any).minH,
      }));
    },
    [generateLayoutForBreakpoint]
  );

  // State to track pending tab change (when user needs to confirm)
  const [pendingTabChange, setPendingTabChange] = React.useState<string | null>(
    null
  );

  // Check if there are unsaved changes (compare with original state before edit mode)
  // This must be defined before handleTabChange so it can be used in the callback
  const hasUnsavedChanges = React.useMemo(() => {
    if (!isEditMode) return false;

    // If reset is pending, there are definitely unsaved changes
    if (pendingReset) return true;

    // Check if card order changed (compare with original order before edit)
    const originalOrder =
      originalCardOrderBeforeReset.length > 0
        ? originalCardOrderBeforeReset
        : visibleCards;
    const orderChanged =
      JSON.stringify(pendingCardOrder || []) !==
      JSON.stringify(originalOrder || []);

    // Check if layouts changed (compare with original layouts before edit)
    const originalLayoutsStr = JSON.stringify(originalLayoutsBeforeEdit || {});
    const pendingLayoutsStr = JSON.stringify(pendingLayouts || {});
    const originalLayoutStr = JSON.stringify(originalLayoutBeforeEdit || []);
    const pendingLayoutStr = JSON.stringify(pendingLayout || []);
    const layoutsChanged =
      pendingLayoutsStr !== originalLayoutsStr ||
      pendingLayoutStr !== originalLayoutStr;

    // Check if heights changed (compare with original heights before edit)
    const originalHeightsStr = JSON.stringify(originalHeightsBeforeEdit || {});
    const pendingHeightsStr = JSON.stringify(pendingHeights || {});
    const heightsChanged = pendingHeightsStr !== originalHeightsStr;

    // Check if widths changed (compare with original widths before edit)
    const originalWidthsStr = JSON.stringify(originalWidthsBeforeEdit || {});
    const pendingWidthsStr = JSON.stringify(pendingWidths || {});
    const widthsChanged = pendingWidthsStr !== originalWidthsStr;

    return orderChanged || layoutsChanged || heightsChanged || widthsChanged;
  }, [
    isEditMode,
    pendingCardOrder,
    originalCardOrderBeforeReset,
    visibleCards,
    pendingLayouts,
    originalLayoutsBeforeEdit,
    pendingLayout,
    originalLayoutBeforeEdit,
    pendingHeights,
    originalHeightsBeforeEdit,
    pendingWidths,
    originalWidthsBeforeEdit,
    pendingReset,
  ]);

  // Memoize tab change handler - moved before useMemo to avoid dependency issues
  const applyPendingTabSwitch = React.useCallback(
    (nextTab: 'manager' | 'my') => {
      setPendingTabChange(null);
      setIsEditMode(false);
      setPendingLayouts({});
      setPendingLayout([]);
      setPendingHeights(null);
      setPendingWidths(null);

      if (nextTab === 'my') {
        setTeamAllocationFilterOpen(false);
        if (pendingViewSwitchTimeoutRef.current) {
          clearTimeout(pendingViewSwitchTimeoutRef.current);
        }
        const VIEW_SWITCH_DELAY_MS = 150;
        pendingViewSwitchTimeoutRef.current = setTimeout(() => {
          pendingViewSwitchTimeoutRef.current = null;
          setDashboardViewType(nextTab);
        }, VIEW_SWITCH_DELAY_MS);
      } else {
        setDashboardViewType(nextTab);
      }
    },
    [setDashboardViewType, setTeamAllocationFilterOpen]
  );

  const openUnsavedTabChangeModal = React.useCallback(
    (targetKey: string) => {
      const nextTab = targetKey as 'manager' | 'my';

      const modalData: ModalData = {
        type: WindowType.WARNING,
        title: t('dashboard.unsaved-title'),
        icon: <img src={GenTaxImage} alt="" />,
        component: (
          <ConfirmBoxContainer
            callback={() => applyPendingTabSwitch(nextTab)}
            callback2={() => {
              setPendingTabChange(null);
              if (nextTab === 'my') {
                setTeamAllocationFilterOpen(false);
              }
              // eslint-disable-next-line react-hooks/exhaustive-deps
              handleSaveClick();
              setTimeout(() => {
                setDashboardViewType(nextTab);
              }, 200);
            }}
            showCancelBtn={false}
            okText={t('vat-report.continue-without-saving')}
            okText2={t('dashboard.save-layout')}
            description={t('dashboard.unsaved-description-switch')}
          />
        ),
        onClose: () => {
          setPendingTabChange(null);
        },
      };
      dispatch(turnModalOn(modalData));
    },
    [
      applyPendingTabSwitch,
      dispatch,
      setDashboardViewType,
      setTeamAllocationFilterOpen,
      t,
    ]
  );

  // CRITICAL: Exit edit mode when switching views to prevent layout conflicts
  // Show warning if there are unsaved changes
  const handleTabChange = React.useCallback(
    (key: string) => {
      // Don't allow tab change if it's the same tab
      if (key === dashboardViewType) {
        return;
      }

      // Check if trying to switch to Manager View without access
      if (key === 'manager' && !hasManagerViewAccess) {
        handleUpgradeClick();
        return;
      }

      // If in edit mode, check for unsaved changes
      if (isEditMode) {
        // Check if there are unsaved changes (recalculate to ensure we have the latest value)
        const hasChanges = hasUnsavedChanges;

        if (hasChanges) {
          setPendingTabChange(key);
          openUnsavedTabChangeModal(key);
          return; // Don't change tab yet - wait for user confirmation
        }

        // No unsaved changes - exit edit mode and switch
        setIsEditMode(false);
        // Clear any pending changes when switching views
        setPendingLayouts({});
        setPendingLayout([]);
        setPendingHeights(null);
        setPendingWidths(null);
      }

      // When switching away from Manager view, close Team Allocation dropdown first
      // then wait so Ant Design Select can tear down (Overflow/ResizeObserver) before unmount and avoid state-update-after-unmount warning
      if (key === 'my') {
        setTeamAllocationFilterOpen(false);
        if (pendingViewSwitchTimeoutRef.current) {
          clearTimeout(pendingViewSwitchTimeoutRef.current);
        }
        const VIEW_SWITCH_DELAY_MS = 150;
        pendingViewSwitchTimeoutRef.current = setTimeout(() => {
          pendingViewSwitchTimeoutRef.current = null;
          setDashboardViewType(key as 'manager' | 'my');
        }, VIEW_SWITCH_DELAY_MS);
        return;
      }

      // Switch to the new tab
      setDashboardViewType(key as 'manager' | 'my');
    },
    [
      setDashboardViewType,
      setTeamAllocationFilterOpen,
      isEditMode,
      hasUnsavedChanges,
      dashboardViewType,
      hasManagerViewAccess,
      handleUpgradeClick,
      openUnsavedTabChangeModal,
    ]
  );

  // Get visible cards - filter based on view type using CARD_REGISTRY
  const filteredVisibleCards = React.useMemo(() => {
    // Safety check: ensure visibleCards is an array
    if (!visibleCards || !Array.isArray(visibleCards)) {
      return [];
    }
    return (visibleCards as CardId[]).filter((cardId) => {
      const cardConfig = CARD_REGISTRY[cardId];
      if (!cardConfig) {
        return false;
      }
      if (!cardConfig.availableInViews.includes(dashboardViewType)) {
        return false;
      }
      return true;
    });
  }, [visibleCards, dashboardViewType]);

  // Get filtered card order - preserves the saved order from cardOrder prop
  // This is critical: we must use cardOrder (which matches saved layouts) not visibleCards
  // When cards are removed, visibleCards may have a different order, but cardOrder preserves the saved order
  const filteredCardOrder = React.useMemo(() => {
    // Safety check: ensure cardOrder and visibleCards are arrays
    if (!cardOrder || !Array.isArray(cardOrder)) {
      return [];
    }
    if (!visibleCards || !Array.isArray(visibleCards)) {
      return [];
    }

    const visibleSet = new Set(
      (visibleCards as CardId[]).filter((cardId) => {
        const cardConfig = CARD_REGISTRY[cardId];
        return (
          cardConfig && cardConfig.availableInViews.includes(dashboardViewType)
        );
      })
    );

    const orderedFromSaved = (cardOrder as CardId[]).filter((cardId) => {
      if (!visibleSet.has(cardId)) {
        return false;
      }
      const cardConfig = CARD_REGISTRY[cardId];
      if (
        !cardConfig ||
        !cardConfig.availableInViews.includes(dashboardViewType)
      ) {
        return false;
      }
      return true;
    });

    const seen = new Set<CardId>(orderedFromSaved);
    const defaultOrder = getDefaultDashboardCardOrderForView(dashboardViewType);

    // Append any visible default cards missing from saved order (e.g. newly added cards after reset)
    const merged = [...orderedFromSaved];
    defaultOrder.forEach((cardId) => {
      if (visibleSet.has(cardId) && !seen.has(cardId)) {
        merged.push(cardId);
        seen.add(cardId);
      }
    });

    return merged;
  }, [cardOrder, visibleCards, dashboardViewType]);

  const viesInfos = useSelector(getViesInfos);
  const viesLoading = useSelector(getViesLoading);

  const showVatCheckerCard = React.useMemo(
    () => filteredCardOrder.includes('vatNumberChecker'),
    [filteredCardOrder]
  );

  React.useEffect(() => {
    if (showVatCheckerCard && entities && entities.length > 0) {
      dispatch(fetchViesInfoRequest(undefined, true));
    }
  }, [showVatCheckerCard, entities, dispatch]);

  const vatCheckerSummary = React.useMemo(
    () => ({
      qualifiedEntityCount: viesInfos?.length ?? 0,
      activeCount:
        viesInfos?.filter((info) => info.vatCheckerActive).length ?? 0,
      entitiesWithInvalidFlags:
        viesInfos?.filter(
          (info) => info.hasInvalidCustomer || info.hasInvalidSupplier
        ).length ?? 0,
      loading: viesLoading,
    }),
    [viesInfos, viesLoading]
  );

  // Initialize pending order when entering edit mode
  React.useEffect(() => {
    if (isEditMode) {
      setPendingCardOrder(visibleCards as CardId[]);
    }
  }, [isEditMode, visibleCards]);

  // Clear justSavedReset and justSaved flags when entering edit mode (fresh start)
  React.useEffect(() => {
    if (isEditMode) {
      setJustSavedReset(false);
      setJustSaved(false);
      savedLayoutsRef.current = null;
    }
  }, [isEditMode]);

  // Clear justSavedReset flag and pendingCardOrder when visibleCards updates to match default (after reset save)
  // This ensures we keep using pendingCardOrder (which has the default order) until the prop updates
  React.useEffect(() => {
    if (
      justSavedReset &&
      pendingCardOrder.length > 0 &&
      !isEditMode &&
      !pendingReset
    ) {
      const defaultOrderForView =
        getDefaultDashboardCardOrderForView(dashboardViewType);
      // Compare visibleCards prop with the default order
      const filteredSorted = [...filteredVisibleCards].sort();
      const defaultSorted = [...defaultOrderForView].sort();
      const pendingSorted = [...pendingCardOrder].sort();
      const cardOrderSorted = [...filteredCardOrder].sort();

      // Check if visibleCards matches default OR if it matches pendingCardOrder (both should be default)
      // If pendingCardOrder matches default (which it should after reset save), we can clear the flag
      // once visibleCards and cardOrder also match (meaning the state has propagated)
      const pendingMatchesDefault =
        JSON.stringify(pendingSorted) === JSON.stringify(defaultSorted);
      const visibleMatchesDefault =
        JSON.stringify(filteredSorted) === JSON.stringify(defaultSorted);
      const visibleMatchesPending =
        JSON.stringify(filteredSorted) === JSON.stringify(pendingSorted);
      const cardOrderMatchesDefault =
        JSON.stringify(cardOrderSorted) === JSON.stringify(defaultSorted);

      // If visibleCards and cardOrder now match default (state has updated), clear the flag
      // We keep using pendingCardOrder and pendingLayouts until this happens to ensure correct display
      if (
        (visibleMatchesDefault && cardOrderMatchesDefault) ||
        (pendingMatchesDefault &&
          visibleMatchesPending &&
          cardOrderMatchesDefault)
      ) {
        // State has updated, clear the flag, pendingCardOrder, pendingLayouts, and ref
        // Use a small delay to ensure the state update has fully propagated
        const timer = setTimeout(() => {
          setJustSavedReset(false);
          setPendingCardOrder([]);
          setPendingLayouts({});
          setPendingLayout([]);
          savedLayoutsRef.current = null;
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [
    justSavedReset,
    pendingCardOrder,
    filteredVisibleCards,
    filteredCardOrder,
    isEditMode,
    pendingReset,
    dashboardViewType,
    getDefaultCardOrder,
  ]);

  // Clear justSaved flag and pendingCardOrder/pendingLayouts when visibleCards updates to match saved order (after regular save)
  // This ensures we keep using pendingCardOrder and pendingLayouts (which have the saved order/layouts) until the prop updates
  React.useEffect(() => {
    if (
      justSaved &&
      pendingCardOrder.length > 0 &&
      !isEditMode &&
      !pendingReset
    ) {
      // Compare visibleCards prop with the pendingCardOrder (which contains what was just saved)
      const filteredSorted = [...filteredVisibleCards].sort();
      const pendingSorted = [...pendingCardOrder].sort();

      // If visibleCards now matches pendingCardOrder (state has updated), clear the flag
      // We keep using pendingCardOrder and pendingLayouts until this happens to ensure correct display
      if (JSON.stringify(filteredSorted) === JSON.stringify(pendingSorted)) {
        // State has updated, clear the flag, pendingCardOrder, pendingLayouts, and ref
        // Use a small delay to ensure the state update has fully propagated
        const timer = setTimeout(() => {
          setJustSaved(false);
          setPendingCardOrder([]);
          setPendingLayouts({});
          setPendingLayout([]);
          savedLayoutsRef.current = null;
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [
    justSaved,
    pendingCardOrder,
    filteredVisibleCards,
    isEditMode,
    pendingReset,
  ]);

  // Handle edit button click
  const handleEditClick = () => {
    // CRITICAL: Always load from preferences first to get the most up-to-date saved state
    // This ensures that when a card is removed and saved, it won't appear in edit mode
    // The visibleCards prop might be stale if edit is clicked immediately after save
    const getCardVisibilityPreferenceKey = (viewType: 'manager' | 'my') => {
      // CRITICAL: Use the same key format as in home/index.tsx
      // The key should match exactly: dashboard-card-visibility_my or dashboard-card-visibility_manager
      return `dashboard-card-visibility_${viewType}`;
    };

    let cardsToUse: CardId[] = [];

    // CRITICAL: Always load from preferences first to get the saved state
    // But if preference is null, use visibleCards prop (which is updated after save)
    // This ensures that when a card is removed and saved, it won't appear even if preference was cleared
    if (currentUser) {
      const savedVisibility = handlePreferences(PreferenceActions.GET, {
        name: getCardVisibilityPreferenceKey(dashboardViewType),
      });
      const defaultOrder = getDefaultCardOrderForView(dashboardViewType);

      if (
        savedVisibility &&
        Array.isArray(savedVisibility) &&
        savedVisibility.length > 0
      ) {
        // Use saved visibility from preferences (most up-to-date)
        cardsToUse = savedVisibility as CardId[];
      } else {
        // CRITICAL: If preference is null, use visibleCards prop instead of defaults
        // The visibleCards prop is updated after save, so it reflects the current state
        // This ensures that when a card is removed and saved, it won't appear even if preference was cleared
        if (
          visibleCards &&
          Array.isArray(visibleCards) &&
          visibleCards.length > 0
        ) {
          cardsToUse = visibleCards as CardId[];
        } else {
          // Fallback to system defaults only if visibleCards is also empty
          cardsToUse = defaultOrder as CardId[];
        }
      }
    } else {
      // Fallback to visibleCards prop if no user
      cardsToUse = visibleCards as CardId[];
    }

    // CRITICAL: Filter out any cards that shouldn't be in the current view
    // This ensures that cards from other views don't appear
    // Use CARD_REGISTRY to check if card is available in current view
    cardsToUse = cardsToUse.filter((cardId) => {
      const cardConfig = CARD_REGISTRY[cardId as CardId];
      if (
        cardConfig &&
        !cardConfig.availableInViews.includes(dashboardViewType)
      ) {
        return false;
      }
      return true;
    });

    // CRITICAL: Ensure cardsToUse only contains cards that should be visible
    // The visibility preference is the source of truth - if a card was removed and saved,
    // it won't be in the saved visibility preference

    // Save current state before entering edit mode (for cancel)
    setOriginalLayoutsBeforeEdit(savedLayouts);
    setOriginalLayoutBeforeEdit(savedLayout);
    setOriginalHeightsBeforeEdit(cardHeights);
    setOriginalWidthsBeforeEdit(cardWidths);
    setOriginalCardOrderBeforeReset(cardsToUse);

    // Initialize pending state with current state
    // CRITICAL: Use cardsToUse (from preferences) instead of visibleCards prop
    // This ensures we always show the latest saved state when entering edit mode

    // CRITICAL: If savedLayouts is empty or incomplete, generate default layouts for all cards
    // This ensures that cards without saved layouts (e.g., removed and re-added) have proper dimensions
    const hasValidSavedLayouts =
      savedLayouts &&
      ((savedLayouts.xl && savedLayouts.xl.length > 0) ||
        (savedLayouts.lg && savedLayouts.lg.length > 0) ||
        (savedLayouts.md && savedLayouts.md.length > 0) ||
        (savedLayouts.sm && savedLayouts.sm.length > 0) ||
        (savedLayouts.xs && savedLayouts.xs.length > 0) ||
        (savedLayouts.xxs && savedLayouts.xxs.length > 0));

    let layoutsToUse = savedLayouts;
    let layoutToUse = savedLayout;

    if (!hasValidSavedLayouts) {
      // Generate default layouts for all cards in cardsToUse
      const defaultLayouts = getDefaultLayouts(cardsToUse);
      layoutsToUse = defaultLayouts;
      layoutToUse = defaultLayouts.lg || defaultLayouts.xl || [];
    } else {
      // Check if all cards in cardsToUse have layout entries
      const lgLayout = savedLayouts.lg || savedLayouts.xl || [];
      const savedCardIds = new Set(lgLayout.map((item: any) => item.i));
      const missingCards = cardsToUse.filter(
        (cardId) => !savedCardIds.has(cardId)
      );

      if (missingCards.length > 0) {
        // Generate default layouts for missing cards and merge with saved layouts
        const defaultLayouts = getDefaultLayouts(cardsToUse);
        const allBreakpoints: Array<'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'> =
          ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'];
        const mergedLayouts: typeof savedLayouts = { ...savedLayouts };

        allBreakpoints.forEach((bp) => {
          const savedForBp = savedLayouts[bp] || [];
          const defaultForBp = defaultLayouts[bp] || [];
          const savedMap = new Map(
            savedForBp.map((item: any) => [item.i, item])
          );

          // Merge: use saved if available, otherwise use default
          const merged = cardsToUse
            .map((cardId) => {
              const saved = savedMap.get(cardId);
              if (saved) {
                // Ensure saved dimensions are valid
                const cardConfig = CARD_REGISTRY[cardId as CardId];
                const minW = cardConfig?.minWidth || 2;
                const minH = cardConfig?.minHeight || 2;
                const defaultW = cardConfig?.defaultWidth || 4;
                const defaultH = cardConfig?.defaultHeight || 4;

                const w =
                  saved.w && typeof saved.w === 'number' && saved.w >= minW
                    ? saved.w
                    : defaultW;
                const h =
                  saved.h && typeof saved.h === 'number' && saved.h >= minH
                    ? saved.h
                    : defaultH;

                const finalW = Math.max(w, minW);
                const finalH = Math.max(h, minH);
                // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
                const finalMinW = Math.min(minW, finalW);

                return {
                  ...saved,
                  w: finalW,
                  h: finalH,
                  minW: saved.minW ? Math.min(saved.minW, finalW) : finalMinW,
                  minH: saved.minH || minH,
                };
              }

              // Use default for missing cards
              const defaultItem = defaultForBp.find(
                (item: any) => item.i === cardId
              );
              if (defaultItem) {
                const cardConfig = CARD_REGISTRY[cardId as CardId];
                const minW = cardConfig?.minWidth || 2;
                const minH = cardConfig?.minHeight || 2;
                const defaultW = cardConfig?.defaultWidth || 4;
                const defaultH = cardConfig?.defaultHeight || 4;

                const w = Math.max(defaultItem.w || defaultW, minW);
                const h = Math.max(defaultItem.h || defaultH, minH);
                // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
                const finalMinW = Math.min(minW, w);

                return {
                  ...defaultItem,
                  w: w,
                  h: h,
                  minW: finalMinW,
                  minH: minH,
                };
              }

              return null;
            })
            .filter(Boolean);

          mergedLayouts[bp] = merged;
        });

        layoutsToUse = mergedLayouts;
        layoutToUse = mergedLayouts.lg || mergedLayouts.xl || [];
      }
    }

    setPendingLayouts(layoutsToUse);
    setPendingLayout(layoutToUse);
    setPendingHeights(cardHeights);
    setPendingWidths(cardWidths);
    setPendingCardOrder(cardsToUse);

    setIsEditMode(true);
  };

  // Handle save button click
  const handleSaveClick = () => {
    // If reset was pending, clear all saved preferences to restore system defaults
    if (pendingReset) {
      // Get the default order that was set during reset preview
      const defaultOrderForView =
        pendingCardOrder.length > 0 ? pendingCardOrder : getDefaultCardOrder();

      // Get the default layouts for the default order
      const defaultLayouts = getDefaultLayouts(defaultOrderForView);

      // IMPORTANT: Store the default layouts in the ref BEFORE any state changes
      // Create a deep copy to ensure the ref doesn't get mutated
      // This prevents GridLayout from recalculating when isEditMode becomes false
      savedLayoutsRef.current = JSON.parse(JSON.stringify(defaultLayouts));

      // IMPORTANT: Set flag BEFORE any state changes to ensure initialLayouts uses the ref immediately
      // This prevents flickering when isEditMode becomes false
      setJustSavedReset(true);

      // Also update pendingLayouts to the default layouts so initialLayouts uses them
      setPendingLayouts(defaultLayouts);
      setPendingLayout(defaultLayouts.lg || defaultLayouts.xl || []);

      // CRITICAL: Clear saved heights and widths on reset so system always uses current cardRegistry.ts defaults
      // This ensures that changes to defaultHeight/defaultWidth in cardRegistry.ts are immediately reflected
      // Save system default layout to user preferences
      saveCardOrder(defaultOrderForView);
      saveCardLayouts(defaultLayouts);
      // Clear saved heights/widths so system defaults from cardRegistry.ts are always used
      saveCardHeights({});
      saveCardWidths({});

      // Also reset visible cards to default - this updates the visibleCards prop
      // We need to do this BEFORE clearing pendingReset so the state updates
      if (resetVisibleCardsToDefault) {
        resetVisibleCardsToDefault();
      }

      // Update saved layout state to default layouts (for immediate use)
      setSavedLayout(defaultLayouts.lg || defaultLayouts.xl || []);
      setSavedLayouts(defaultLayouts);

      // Clear pending heights and widths (use system defaults)
      setPendingHeights(null);
      setPendingWidths(null);
      setPendingReset(false);

      // Force a re-render by ensuring pendingCardOrder is set to the default
      // This ensures cardsForLayout uses the correct order immediately
      if (pendingCardOrder.length === 0) {
        setPendingCardOrder(defaultOrderForView);
      }

      // Show success notification after a delay to allow layout to settle
      setTimeout(() => {
        const resetMessage: MessageData = {
          type: MessageStates.SUCCESS,
          title: `${t('common.success')}!`,
          description: t('dashboard.layout-reset-success'),
          duration: 3,
        };
        dispatch(turnMessageOn(resetMessage));
      }, 200);
    } else {
      // CRITICAL: Save card visibility first (this also handles layouts, heights, widths)
      // This ensures that removed cards are filtered out and added cards get proper positions
      const currentPendingLayouts =
        Object.keys(pendingLayouts).length > 0
          ? pendingLayouts
          : Object.keys(savedLayouts).length > 0
          ? savedLayouts
          : null;

      saveCardVisibilityBatch(
        pendingCardOrder.map((c) => c as string),
        currentPendingLayouts || undefined
      );

      // Save the pending card order (saveCardVisibilityBatch also does this, but we keep it for clarity)
      saveCardOrder(pendingCardOrder.map((c) => c as string));

      // Save pending layouts - always save layouts if there's any data
      // Priority: pendingLayouts > pendingLayout > savedLayouts > savedLayout
      // IMPORTANT: If pendingLayouts is empty, we need to get the current layout from initialLayouts
      // This ensures we save the current positions even if handleLayoutChange wasn't called
      let layoutsToSave: {
        xl?: any[];
        lg?: any[];
        md?: any[];
        sm?: any[];
        xs?: any[];
        xxs?: any[];
      } | null = null;

      if (Object.keys(pendingLayouts).length > 0) {
        layoutsToSave = pendingLayouts;
        setSavedLayouts(pendingLayouts);
        // Also update savedLayout for backward compatibility
        if (pendingLayout.length > 0) {
          setSavedLayout(pendingLayout);
        } else {
          // If pendingLayout is empty, use the lg layout from pendingLayouts
          setSavedLayout(pendingLayouts.lg || pendingLayouts.xl || []);
        }
      } else if (pendingLayout.length > 0) {
        // If pendingLayouts is empty but pendingLayout has data, construct layouts from pendingLayout
        layoutsToSave = {
          xl: pendingLayout,
          lg: pendingLayout,
          md: pendingLayout,
          sm: pendingLayout,
          xs: pendingLayout,
          xxs: pendingLayout,
        };
        setSavedLayouts(layoutsToSave);
        setSavedLayout(pendingLayout);
      } else if (Object.keys(savedLayouts).length > 0) {
        // If no pending layouts but we have saved layouts, ensure they're persisted
        layoutsToSave = savedLayouts;
      } else if (savedLayout.length > 0) {
        // If we have savedLayout but no savedLayouts, construct and save
        layoutsToSave = {
          xl: savedLayout,
          lg: savedLayout,
          md: savedLayout,
          sm: savedLayout,
          xs: savedLayout,
          xxs: savedLayout,
        };
        setSavedLayouts(layoutsToSave);
      } else {
        // If we have no pending or saved layouts, use savedLayouts if available
        // Otherwise, this shouldn't happen in normal flow, but we'll use defaults as fallback
        if (Object.keys(savedLayouts).length > 0) {
          layoutsToSave = savedLayouts;
        } else {
          // Last resort: generate default layouts (shouldn't happen in normal flow)
          const currentLayouts = getDefaultLayouts(cardsForLayout);
          layoutsToSave = currentLayouts;
          setSavedLayouts(currentLayouts);
          setSavedLayout(currentLayouts.lg || currentLayouts.xl || []);
        }
      }

      // Always save layouts if we have any to save
      if (layoutsToSave && Object.keys(layoutsToSave).length > 0) {
        // IMPORTANT: Store the saved layout in a ref BEFORE any state changes or API calls
        // Create a deep copy to ensure the ref doesn't get mutated
        // This prevents GridLayout from recalculating when isEditMode becomes false
        savedLayoutsRef.current = JSON.parse(JSON.stringify(layoutsToSave));

        saveCardLayouts(layoutsToSave);
        // Also update pendingLayouts and savedLayouts
        setSavedLayouts(layoutsToSave);
        setPendingLayouts(layoutsToSave);
        if (pendingLayout.length === 0) {
          setPendingLayout(layoutsToSave.lg || layoutsToSave.xl || []);
        }
      } else {
        // If we don't have layouts to save, ensure we have a layout in the ref
        // Use current pendingLayouts if available, otherwise use savedLayouts or defaults
        const fallbackLayouts =
          Object.keys(pendingLayouts).length > 0
            ? pendingLayouts
            : Object.keys(savedLayouts).length > 0
            ? savedLayouts
            : getDefaultLayouts(cardsForLayout);
        savedLayoutsRef.current = JSON.parse(JSON.stringify(fallbackLayouts));
      }

      // Save pending heights and widths
      if (pendingHeights && Object.keys(pendingHeights).length > 0) {
        saveCardHeights(pendingHeights);
      }
      if (pendingWidths && Object.keys(pendingWidths).length > 0) {
        saveCardWidths(pendingWidths);
      }

      // Set flag to indicate we just saved - this will keep using pendingCardOrder
      // until visibleCards prop updates to match what was saved
      // IMPORTANT: Set this BEFORE setIsEditMode(false) to prevent layout recalculation
      setJustSaved(true);

      // Show success notification for save
      const saveMessage: MessageData = {
        title: `${t('common.success')}!`,
        description: t('dashboard.layout-saved-success'),
        type: MessageStates.SUCCESS,
      };
      dispatch(turnMessageOn(saveMessage));
    }

    // Clear pending state
    // Note: We don't clear pendingCardOrder and pendingLayouts immediately after reset save or regular save
    // because cardsForLayout and initialLayouts need to keep using them until visibleCards prop updates
    // They will be cleared when visibleCards matches the saved order (see cardsForLayout logic)
    if (!pendingReset && !justSaved) {
      setPendingCardOrder([]);
      setPendingLayouts({});
      setPendingLayout([]);
    }
    setPendingHeights(null);
    setPendingWidths(null);
    setOriginalLayoutsBeforeEdit({});
    setOriginalLayoutBeforeEdit([]);
    setOriginalHeightsBeforeEdit(null);
    setOriginalWidthsBeforeEdit(null);

    // Set edit mode to false AFTER setting justSaved flag
    // This ensures initialLayouts continues using pendingLayouts
    setIsEditMode(false);
  };

  // Handle navigation away warning (when user tries to leave the page)
  const handleNavigationAwayWarning = React.useCallback(
    (targetLocation: any) => {
      const continueNavigation = () => {
        allowNextNavigationRef.current = true;
        dispatch(turnModalOff());
        navigate(targetLocation.pathname);
      };

      const saveAndNavigate = () => {
        // Save the layout first - perform all save operations
        if (pendingReset) {
          // CRITICAL: Save system default layout to user preferences
          // pendingLayouts and pendingCardOrder should already contain the default layouts/order
          // from handleResetClick, so we can use them to save the defaults
          if (
            pendingLayouts &&
            Object.keys(pendingLayouts).length > 0 &&
            pendingCardOrder.length > 0
          ) {
            // Extract default heights and widths from the default layouts
            const defaultHeights: Record<string, number> = {};
            const defaultWidths: Record<string, number> = {};

            // Use lg layout as reference for heights and widths (most common breakpoint)
            const lgLayout = pendingLayouts.lg || pendingLayouts.xl || [];
            lgLayout.forEach((item) => {
              if (item.h && typeof item.h === 'number') {
                defaultHeights[item.i] = item.h;
              }
              if (item.w && typeof item.w === 'number') {
                defaultWidths[item.i] = item.w;
              }
            });

            // Save system defaults to preferences so they persist on refresh
            saveCardOrder(pendingCardOrder.map((c) => c as string));
            saveCardLayouts(pendingLayouts);
            // Clear saved heights/widths so system defaults from cardRegistry.ts are always used
            saveCardHeights({});
            saveCardWidths({});
          } else {
            // Fallback: Get defaults if pendingLayouts is not available
            const currentDefaultOrder = getDefaultCardOrder();
            const defaultOrderForView = currentDefaultOrder.filter((cardId) => {
              if (
                cardId === 'yourRoleInReturns' &&
                dashboardViewType !== 'my'
              ) {
                return false;
              }
              if (
                cardId === 'teamAllocation' &&
                dashboardViewType !== 'manager'
              ) {
                return false;
              }
              if (
                cardId === 'timeToSubmit' &&
                dashboardViewType !== 'manager'
              ) {
                return false;
              }
              return true;
            });
            const defaultLayouts = getDefaultLayouts(defaultOrderForView);

            const defaultHeights: Record<string, number> = {};
            const defaultWidths: Record<string, number> = {};
            const lgLayout = defaultLayouts.lg || defaultLayouts.xl || [];
            lgLayout.forEach((item) => {
              if (item.h && typeof item.h === 'number') {
                defaultHeights[item.i] = item.h;
              }
              if (item.w && typeof item.w === 'number') {
                defaultWidths[item.i] = item.w;
              }
            });

            saveCardOrder(defaultOrderForView);
            saveCardLayouts(defaultLayouts);
            // Clear saved heights/widths so system defaults from cardRegistry.ts are always used
            saveCardHeights({});
            saveCardWidths({});
          }

          setPendingReset(false);
          setSavedLayout(pendingLayouts?.lg || pendingLayouts?.xl || []);
          setSavedLayouts(pendingLayouts || {});

          // Show success notification after a delay to allow layout to settle
          setTimeout(() => {
            const resetMessage: MessageData = {
              type: MessageStates.SUCCESS,
              title: `${t('common.success')}!`,
              description: t('dashboard.layout-reset-success'),
            };
            dispatch(turnMessageOn(resetMessage));
          }, 200);
        } else {
          // Save the pending card order
          saveCardOrder(pendingCardOrder.map((c) => c as string));

          // CRITICAL: Save pending layouts to user preferences
          // This ensures card locations are saved and loaded from user preferences
          // My View and Manager View have separate preference keys (dashboard-card-layouts_my vs dashboard-card-layouts_manager)
          // After save, card locations are persisted and will be loaded when the page is opened
          // RULE: Once reset is used or saved is used, the card location is then saved and loaded from user preferences
          let layoutsToSave: {
            xl?: any[];
            lg?: any[];
            md?: any[];
            sm?: any[];
            xs?: any[];
            xxs?: any[];
          } | null = null;

          if (Object.keys(pendingLayouts).length > 0) {
            // Use pending layouts if available (user moved/resized cards)
            layoutsToSave = pendingLayouts;
            setSavedLayouts(pendingLayouts);
            if (pendingLayout.length > 0) {
              setSavedLayout(pendingLayout);
            }
          } else if (pendingLayout.length > 0) {
            // If pendingLayouts is empty but pendingLayout has data, construct layouts from pendingLayout
            layoutsToSave = {
              xl: pendingLayout,
              lg: pendingLayout,
              md: pendingLayout,
              sm: pendingLayout,
              xs: pendingLayout,
              xxs: pendingLayout,
            };
            setSavedLayouts(layoutsToSave);
            setSavedLayout(pendingLayout);
          } else if (Object.keys(savedLayouts).length > 0) {
            // If no pending layouts, use current saved layouts (fallback)
            layoutsToSave = savedLayouts;
          } else if (savedLayout.length > 0) {
            // Last resort: construct from savedLayout
            layoutsToSave = {
              xl: savedLayout,
              lg: savedLayout,
              md: savedLayout,
              sm: savedLayout,
              xs: savedLayout,
              xxs: savedLayout,
            };
          }

          // CRITICAL: Always save layouts to user preferences if we have any layout data
          // This ensures that after moving cards and clicking save, the positions are persisted
          // and will be loaded when the page is refreshed
          if (layoutsToSave && Object.keys(layoutsToSave).length > 0) {
            // Save to user preferences - this persists the card matrix positions (x, y) for each card
            saveCardLayouts(layoutsToSave);
          }

          // Save pending heights and widths
          if (pendingHeights && Object.keys(pendingHeights).length > 0) {
            saveCardHeights(pendingHeights);
          }
          if (pendingWidths && Object.keys(pendingWidths).length > 0) {
            saveCardWidths(pendingWidths);
          }

          // Show success notification for save
          const saveMessage: MessageData = {
            title: `${t('common.success')}!`,
            description: t('dashboard.layout-saved-success'),
            type: MessageStates.SUCCESS,
          };
          dispatch(turnMessageOn(saveMessage));
        }

        // Clear pending state
        setPendingLayouts({});
        setPendingLayout([]);
        setPendingHeights(null);
        setPendingWidths(null);
        setOriginalLayoutsBeforeEdit({});
        setOriginalLayoutBeforeEdit([]);
        setOriginalHeightsBeforeEdit(null);
        setOriginalWidthsBeforeEdit(null);
        setIsEditMode(false);

        // Close modal and navigate after save operations complete
        // Use requestAnimationFrame to ensure state updates are processed
        requestAnimationFrame(() => {
          setTimeout(() => {
            allowNextNavigationRef.current = true;
            dispatch(turnModalOff());
            navigate(targetLocation.pathname);
          }, 200);
        });
      };

      if (hasUnsavedChanges) {
        const modalData: ModalData = {
          type: WindowType.WARNING,
          title: t('dashboard.unsaved-title'),
          icon: <img src={GenTaxImage} alt="" />,
          component: (
            <ConfirmBoxContainer
              callback={continueNavigation}
              callback2={saveAndNavigate}
              showCancelBtn={false}
              okText={t('vat-report.continue-without-saving')}
              okText2={t('dashboard.save-layout')}
              description={t('submissions.mapping-go-back-description')}
            />
          ),
        };
        dispatch(turnModalOn(modalData));
      } else {
        // No unsaved changes, allow navigation
        navigate(targetLocation.pathname);
      }
    },
    [hasUnsavedChanges, dispatch, navigate, handleSaveClick]
  );

  const blocker = useHistoryBlock((targetLocation) => {
    if (allowNextNavigationRef.current) {
      allowNextNavigationRef.current = false;
      return true;
    }
    if (!(isEditMode && hasUnsavedChanges)) return true;
    handleNavigationAwayWarning(targetLocation);
    return false;
  });

  // Show unsaved changes warning (matching mapping and submission pages)
  const showUnsavedChangesWarning = (onConfirm: () => void) => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('dashboard.unsaved-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={onConfirm}
          callback2={handleSaveClick}
          showCancelBtn={false}
          okText={t('vat-report.continue-without-saving')}
          okText2={t('dashboard.save-layout')}
          description={t('submissions.mapping-go-back-description')}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      showUnsavedChangesWarning(() => {
        // User chose to continue without saving - proceed with cancel
        handleCancelClickInternal();
      });
      return;
    }

    // No unsaved changes - proceed with cancel
    handleCancelClickInternal();
  };

  // Internal cancel handler (actual cancel logic)
  const handleCancelClickInternal = () => {
    // If reset was pending, restore to original state before reset (ignore reset)
    if (pendingReset) {
      // Restore the user's saved layouts (from preferences) that were saved before reset
      if (Object.keys(originalLayoutsBeforeEdit).length > 0) {
        setSavedLayouts(originalLayoutsBeforeEdit);
        setSavedLayout(originalLayoutBeforeEdit);
      } else {
        // If no saved layouts, clear them
        setSavedLayouts({});
        setSavedLayout([]);
      }

      // Restore card order
      setPendingCardOrder(originalCardOrderBeforeReset);

      // Clear pending state since we're exiting edit mode
      setPendingLayouts({});
      setPendingLayout([]);
      setPendingHeights(null);
      setPendingWidths(null);

      setPendingReset(false);
      setOriginalCardOrderBeforeReset([]);
      setOriginalLayoutsBeforeEdit({});
      setOriginalLayoutBeforeEdit([]);
      setOriginalHeightsBeforeEdit(null);
      setOriginalWidthsBeforeEdit(null);
    } else {
      // Restore to original state before entering edit mode (ignore all changes)
      // CRITICAL: Restore original card order (visibility) to ensure added cards are removed
      const originalOrder = (
        originalCardOrderBeforeReset.length > 0
          ? originalCardOrderBeforeReset
          : visibleCards
      ) as CardId[];
      setPendingCardOrder(originalOrder);
      setPendingLayouts(originalLayoutsBeforeEdit);
      setPendingLayout(originalLayoutBeforeEdit);
      setPendingHeights(originalHeightsBeforeEdit);
      setPendingWidths(originalWidthsBeforeEdit);

      // CRITICAL: Also restore the original visibility in the parent component
      // This ensures that when cancel is clicked, added cards disappear immediately
      // The parent component's visibleCards prop should reflect the original state
      // Note: We can't directly update visibleCards here, but the parent should handle this
      // by checking if we're exiting edit mode and restoring the saved visibility

      // Clear original state
      setOriginalCardOrderBeforeReset([]);
      setOriginalLayoutsBeforeEdit({});
      setOriginalLayoutBeforeEdit([]);
      setOriginalHeightsBeforeEdit(null);
      setOriginalWidthsBeforeEdit(null);
    }

    setIsEditMode(false);
  };

  // Handle reset button click - restore default layout and automatically save
  const handleResetClick = () => {
    // Get the default order for current view
    const defaultOrderForView =
      getDefaultDashboardCardOrderForView(dashboardViewType);

    // Get the default layouts for the default order
    const defaultLayouts = getDefaultLayouts(defaultOrderForView);

    // IMPORTANT: Store the default layouts in the ref BEFORE any state changes
    // Create a deep copy to ensure the ref doesn't get mutated
    // This prevents GridLayout from recalculating when isEditMode becomes false
    savedLayoutsRef.current = JSON.parse(JSON.stringify(defaultLayouts));

    // IMPORTANT: Set flag BEFORE any state changes to ensure initialLayouts uses the ref immediately
    // This prevents flickering when isEditMode becomes false
    setJustSavedReset(true);

    // Update pending state to default values
    setPendingCardOrder(defaultOrderForView as CardId[]);
    setPendingLayouts(defaultLayouts);
    setPendingLayout(defaultLayouts.lg || defaultLayouts.xl || []);

    // Update saved layouts state to default layouts (for immediate use)
    setSavedLayouts(defaultLayouts);
    setSavedLayout(defaultLayouts.lg || defaultLayouts.xl || []);

    // CRITICAL: Save system default layout to user preferences
    // This ensures the system defaults are persisted and loaded on refresh
    // Extract default heights and widths from the default layouts
    const defaultHeights: Record<string, number> = {};
    const defaultWidths: Record<string, number> = {};

    // Use lg layout as reference for heights and widths (most common breakpoint)
    const lgLayout = defaultLayouts.lg || defaultLayouts.xl || [];
    lgLayout.forEach((item) => {
      if (item.h && typeof item.h === 'number') {
        defaultHeights[item.i] = item.h;
      }
      if (item.w && typeof item.w === 'number') {
        defaultWidths[item.i] = item.w;
      }
    });

    // CRITICAL: Reset visible cards FIRST - this clears both visibility and order preferences
    // This ensures that all previously hidden cards are shown again after reset
    if (resetVisibleCardsToDefault) {
      resetVisibleCardsToDefault();
    }

    if (currentUser?.userUuid) {
      setPreferencesInSection(
        PreferenceSections.APP,
        [{ name: DOCUMENT_CAPTURE_BANNER_HIDE, value: false }],
        currentUser,
        dispatch,
        () => getStoredPreferenceDetails(store.getState())
      );
    }

    // Persist default card order so layout rendering stays in sync after reset
    saveCardOrder(defaultOrderForView.map((c) => c as string));

    // Save system defaults to preferences so they persist on refresh
    // Note: cardOrder is already cleared by resetVisibleCardsToDefault above
    saveCardLayouts(defaultLayouts);
    // Clear saved heights/widths so system defaults from cardRegistry.ts are always used
    saveCardHeights({});
    saveCardWidths({});

    // DON'T clear savedLayout and savedLayouts here - we just set them to defaultLayouts above
    // This ensures the layout uses the default positions immediately

    // Clear pending heights and widths (use system defaults)
    setPendingHeights(null);
    setPendingWidths(null);

    // Clear original state (no longer needed since we're saving immediately)
    setOriginalLayoutsBeforeEdit({});
    setOriginalLayoutBeforeEdit([]);
    setOriginalHeightsBeforeEdit(null);
    setOriginalWidthsBeforeEdit(null);

    // Note: We don't clear pendingCardOrder and pendingLayouts immediately
    // because cardsForLayout and initialLayouts need to keep using them until visibleCards prop updates
    // They will be cleared when visibleCards matches the default order (see useEffect logic)

    // Exit edit mode AFTER setting all state - this will trigger the layout to use the ref
    // Use setTimeout to ensure state updates are processed first
    setTimeout(() => {
      setIsEditMode(false);
    }, 0);
  };

  // Calculate container width based on screen size (uses debounced windowWidth)
  const containerWidth = React.useMemo(() => {
    // Container has maxWidth: 1600 and padding: 40px (80px total horizontal)
    // Grid width should be container maxWidth minus horizontal padding
    const maxContainerWidth = 1600;
    const horizontalPadding = 80; // 40px on each side

    // Grid should never exceed container's maxWidth minus padding
    // This ensures cards stay within the 12-column grid and don't expand beyond page width
    if (windowWidth >= 1600) {
      return maxContainerWidth - horizontalPadding; // 1520px max
    }
    if (windowWidth >= 1400) {
      return Math.min(windowWidth - 200, maxContainerWidth - horizontalPadding);
    }
    return Math.max(windowWidth - 160, 1200 - horizontalPadding); // Minimum responsive width
  }, [windowWidth]);

  // Generate layout from visible cards (use pending order in edit mode or when reset is pending)
  // When reset is pending, also update visible cards to show preview immediately
  // After reset save, continue using pendingCardOrder until visibleCards prop updates to match
  // CRITICAL: Always use cardOrder (filtered) instead of filteredVisibleCards to preserve saved order
  // This prevents cards from moving when the order in visibleCards changes after card removal
  const cardsForLayout = React.useMemo(() => {
    let cards: CardId[] = [];

    // If we have a pending card order and we're in edit mode, use it
    if (isEditMode && pendingCardOrder.length > 0) {
      cards = pendingCardOrder;
    }
    // If reset is pending, use pendingCardOrder for preview
    else if (pendingReset && pendingCardOrder.length > 0) {
      cards = pendingCardOrder;
    }
    // After reset save, if we just saved a reset, keep using pendingCardOrder until visibleCards updates
    // The pendingCardOrder should contain the default order that was set during reset
    else if (justSavedReset && pendingCardOrder.length > 0) {
      // Always use pendingCardOrder after reset save - it contains the correct default order
      // The useEffect will clear justSavedReset when visibleCards prop updates to match
      cards = pendingCardOrder;
    }
    // After regular save, if we just saved, keep using pendingCardOrder until visibleCards updates
    // The pendingCardOrder should contain the order that was just saved
    else if (justSaved && pendingCardOrder.length > 0) {
      // Always use pendingCardOrder after save - it contains the correct saved order
      // The useEffect will clear justSaved when visibleCards prop updates to match
      cards = pendingCardOrder;
    }
    // CRITICAL: Use filteredCardOrder (from cardOrder prop) instead of filteredVisibleCards
    // cardOrder preserves the saved order, while visibleCards may have a different order after card removal
    // This ensures the default layout generation uses the same order as the saved layouts
    else {
      // Safety check: ensure both are arrays before using them
      const hasFilteredCardOrder =
        filteredCardOrder &&
        Array.isArray(filteredCardOrder) &&
        filteredCardOrder.length > 0;
      const hasFilteredVisibleCards =
        filteredVisibleCards &&
        Array.isArray(filteredVisibleCards) &&
        filteredVisibleCards.length > 0;

      cards = hasFilteredCardOrder
        ? filteredCardOrder
        : hasFilteredVisibleCards
        ? filteredVisibleCards
        : [];
    }

    // Safety check: ensure cards is an array before filtering
    if (!cards || !Array.isArray(cards)) {
      return [];
    }

    // CRITICAL: Always filter by view type to ensure cards from wrong view don't appear
    const viewFiltered = cards.filter((cardId) => {
      const cardConfig = CARD_REGISTRY[cardId as CardId];
      if (
        !cardConfig ||
        !cardConfig.availableInViews.includes(dashboardViewType)
      ) {
        return false;
      }
      return true;
    });

    const forView = pwaMobileNav
      ? filterPwaDashboardCards(viewFiltered)
      : viewFiltered;

    // Edit mode keeps drag order; otherwise KPIs stay above the large info cards.
    if (isEditMode || pendingReset) {
      return forView;
    }
    return getCanonicalDashboardCardOrder(forView);
  }, [
    pendingReset,
    isEditMode,
    pendingCardOrder,
    filteredCardOrder,
    filteredVisibleCards,
    dashboardViewType,
    getDefaultCardOrder,
    justSavedReset,
    justSaved,
    pwaMobileNav,
  ]);

  // Generate initial layouts for all breakpoints
  const initialLayouts = React.useMemo(() => {
    if (
      pwaMobileNav &&
      cardsForLayout &&
      Array.isArray(cardsForLayout) &&
      cardsForLayout.length > 0
    ) {
      const pwaLayouts = getPwaMobileDashboardLayouts(cardsForLayout);
      currentLayoutRef.current = pwaLayouts;
      return pwaLayouts;
    }

    // After regular save, if we just saved, ALWAYS use the saved layout from ref
    // This prevents the layout from changing when isEditMode becomes false
    if (justSaved && savedLayoutsRef.current) {
      const calendarInRef = savedLayoutsRef.current.lg?.find(
        (item: any) => item.i === 'calendar'
      );
      currentLayoutRef.current = savedLayoutsRef.current;
      return savedLayoutsRef.current;
    }

    // After reset save, if we just saved a reset, ALWAYS use the saved layout from ref
    // This prevents the layout from changing when isEditMode becomes false
    if (justSavedReset && savedLayoutsRef.current) {
      const calendarInRef = savedLayoutsRef.current.lg?.find(
        (item: any) => item.i === 'calendar'
      );
      currentLayoutRef.current = savedLayoutsRef.current;
      return savedLayoutsRef.current;
    }

    // CRITICAL: In edit mode, ALWAYS use pending layouts if available
    // This prevents cards from moving when other cards are removed
    // When a card is removed, pendingLayouts should contain the current positions of remaining cards
    // This ensures cards stay locked in their positions even during card removal
    // IMPORTANT: Even if cardLayouts is null, we should use pendingLayouts to preserve current positions
    // Check if pendingLayouts has actual layout arrays (not just empty object)
    const hasPendingLayouts =
      pendingLayouts &&
      ((pendingLayouts.xl && pendingLayouts.xl.length > 0) ||
        (pendingLayouts.lg && pendingLayouts.lg.length > 0) ||
        (pendingLayouts.md && pendingLayouts.md.length > 0) ||
        (pendingLayouts.sm && pendingLayouts.sm.length > 0) ||
        (pendingLayouts.xs && pendingLayouts.xs.length > 0) ||
        (pendingLayouts.xxs && pendingLayouts.xxs.length > 0));
    if (isEditMode && hasPendingLayouts) {
      // CRITICAL: Store in currentLayoutRef for use when removing cards
      currentLayoutRef.current = pendingLayouts;
      return pendingLayouts;
    }

    // CRITICAL: Even when not in edit mode, if we have pendingLayouts and no saved layouts,
    // use pendingLayouts to preserve positions. This can happen when a card is removed
    // and cardLayouts is null but we have pendingLayouts from the previous state.
    // This prevents cards from jumping to default positions when cardLayouts hasn't been updated yet
    // IMPORTANT: Only use pendingLayouts if we're still in the same view (not after view switch)
    // When switching views, pendingLayouts is cleared, so this should only apply within the same view
    const hasPendingLayoutsForNonEdit =
      pendingLayouts &&
      ((pendingLayouts.xl && pendingLayouts.xl.length > 0) ||
        (pendingLayouts.lg && pendingLayouts.lg.length > 0) ||
        (pendingLayouts.md && pendingLayouts.md.length > 0) ||
        (pendingLayouts.sm && pendingLayouts.sm.length > 0) ||
        (pendingLayouts.xs && pendingLayouts.xs.length > 0) ||
        (pendingLayouts.xxs && pendingLayouts.xxs.length > 0));
    // Only use pendingLayouts if cardLayouts is null AND we have no savedLayouts
    // This ensures we don't use stale pendingLayouts from a different view
    // When switching views, cardLayouts should be loaded for the new view, so this won't trigger
    if (
      !isEditMode &&
      hasPendingLayoutsForNonEdit &&
      !cardLayouts &&
      (!savedLayouts || Object.keys(savedLayouts).length === 0) &&
      (!savedLayout || savedLayout.length === 0)
    ) {
      currentLayoutRef.current = pendingLayouts;
      return pendingLayouts;
    }

    // If reset is pending, use pending layouts (which contain default positions)
    // CRITICAL: Use savedLayoutsRef if available to prevent jump to first position
    // This ensures cards like Calendar don't jump to (0,0) before going to reset position
    if (pendingReset) {
      if (
        savedLayoutsRef.current &&
        Object.keys(savedLayoutsRef.current).length > 0
      ) {
        // Use ref to prevent visual jump - ref contains the default layouts we just set
        return savedLayoutsRef.current;
      }
      if (Object.keys(pendingLayouts).length > 0) {
        return pendingLayouts;
      }
    }

    // CRITICAL: Always try to use saved layouts to preserve positions
    // Priority: cardLayouts (from preferences) > savedLayouts (from state) > savedLayout (backward compat)
    // This ensures cards like Calendar and Returns by Due Date keep their positions
    // RULE: Cards should NEVER move - they must use saved positions or system defaults
    let layoutsToMerge: {
      xl?: any[];
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    } | null = null;

    // First try cardLayouts from preferences (most reliable - persisted to user preferences)
    // cardLayouts contains the saved matrix positions for the current view (My View or Manager View)
    // Each view has separate preference keys: dashboard-card-layouts_my vs dashboard-card-layouts_manager
    // These are loaded when the view is switched and contain the exact grid positions (x, y) for each card
    if (cardLayouts && (cardLayouts.lg || cardLayouts.xl)) {
      // CRITICAL: Filter saved layouts to only include cards that are in cardsForLayout
      // This ensures that removed cards don't appear even if they're still in saved layouts
      // The saved layouts will be cleaned up when cards are removed, but we need to filter here too
      const allBreakpoints: Array<'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'> = [
        'xl',
        'lg',
        'md',
        'sm',
        'xs',
        'xxs',
      ];
      const completeLayouts: {
        xl?: any[];
        lg?: any[];
        md?: any[];
        sm?: any[];
        xs?: any[];
        xxs?: any[];
      } = { ...cardLayouts };
      allBreakpoints.forEach((bp) => {
        if (!completeLayouts[bp] && completeLayouts.lg) {
          // Use lg as fallback for missing breakpoints
          completeLayouts[bp] = completeLayouts.lg;
        }
        // CRITICAL: Filter to only include cards that are in cardsForLayout
        // This prevents removed cards from appearing
        if (completeLayouts[bp]) {
          const cardsForLayoutSet = new Set(cardsForLayout);
          completeLayouts[bp] = completeLayouts[bp]!.filter((item: any) =>
            cardsForLayoutSet.has(item.i)
          );
        }
      });
      const calendarInCardLayouts = completeLayouts.lg?.find(
        (item: any) => item.i === 'calendar'
      );
      layoutsToMerge = completeLayouts;
    }
    // Then try savedLayouts from state (temporary state before save)
    else if (savedLayouts && (savedLayouts.lg || savedLayouts.xl)) {
      // CRITICAL: Ensure ALL breakpoints are present (xl, lg, md, sm, xs, xxs)
      // If a breakpoint is missing, use lg as fallback (most common)
      const allBreakpoints: Array<'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'> = [
        'xl',
        'lg',
        'md',
        'sm',
        'xs',
        'xxs',
      ];
      const completeLayouts: {
        xl?: any[];
        lg?: any[];
        md?: any[];
        sm?: any[];
        xs?: any[];
        xxs?: any[];
      } = { ...savedLayouts };
      allBreakpoints.forEach((bp) => {
        if (!completeLayouts[bp] && completeLayouts.lg) {
          // Use lg as fallback for missing breakpoints
          completeLayouts[bp] = completeLayouts.lg;
        }
        // CRITICAL: Filter to only include cards that are in cardsForLayout
        // This prevents removed cards from appearing
        if (completeLayouts[bp]) {
          const cardsForLayoutSet = new Set(cardsForLayout);
          completeLayouts[bp] = completeLayouts[bp]!.filter((item: any) =>
            cardsForLayoutSet.has(item.i)
          );
        }
      });
      layoutsToMerge = completeLayouts;
    }
    // Finally try savedLayout (backward compatibility)
    else if (savedLayout && savedLayout.length > 0) {
      layoutsToMerge = {
        xl: savedLayout,
        lg: savedLayout,
        md: savedLayout,
        sm: savedLayout,
        xs: savedLayout,
        xxs: savedLayout,
      };
    }

    // CRITICAL: Generate system defaults for current view
    // System defaults are view-specific (My View vs Manager View have different defaults)
    // System defaults come from getDefaultLayouts() which uses dashboardViewType
    // System defaults are NEVER stored in preferences - they are hardcoded in code
    // IMPORTANT: Always use cardsForLayout (from props) for default layout generation
    // DO NOT extract order from saved layouts - saved layouts already have correct positions
    // Using saved order to generate defaults can cause cards to be placed at wrong positions
    // (e.g., if calendar is first in saved order, it would be placed at 0,0 in defaults)
    const defaultLayouts = getDefaultLayouts(cardsForLayout);
    const calendarInDefault = defaultLayouts.lg?.find(
      (item: any) => item.i === 'calendar'
    );

    // Note: pendingReset check is already handled above using savedLayoutsRef
    // This ensures cards don't jump to (0,0) before going to reset position

    // CRITICAL: If no saved layouts in preferences, use system defaults directly
    // System defaults are hardcoded in getDefaultLayouts() and should NEVER be stored in preferences
    // This ensures the system can always return to the default state
    // IMPORTANT: System defaults are view-specific (My View vs Manager View have different defaults)
    // The getDefaultLayouts() function uses dashboardViewType to generate view-specific defaults
    if (!layoutsToMerge) {
      // No saved layouts in preferences - use system defaults from code
      // System defaults are view-specific and cannot be overridden
      // CRITICAL: Store system defaults in currentLayoutRef for use when removing cards
      // This ensures we have positions to preserve even if no saved layouts exist
      currentLayoutRef.current = defaultLayouts;
      return defaultLayouts;
    }

    // Merge saved layouts with defaults for missing cards
    // CRITICAL RULE: Cards should NEVER move automatically - they are LOCKED to their saved positions
    // When cards are removed, remaining cards MUST keep their exact saved positions (x, y)
    // This prevents any automatic movement when a card is removed
    // Cards should only move if explicitly moved by the user in edit mode
    const mergeLayouts = (
      saved: any[] | undefined,
      defaultLayout: any[],
      breakpoint: string
    ) => {
      if (!saved || saved.length === 0) {
        // No saved layouts - use system defaults
        // System defaults are view-specific and come from getDefaultLayouts()
        const calendarInDefault = defaultLayout.find((d) => d.i === 'calendar');
        if (breakpoint === 'lg') {
        }
        return defaultLayout;
      }
      const savedMap = new Map(saved.map((l) => [l.i, l]));
      const calendarSaved = savedMap.get('calendar');
      const calendarDefault = defaultLayout.find((d) => d.i === 'calendar');
      if (breakpoint === 'lg') {
      }
      const merged = cardsForLayout.map((cardId) => {
        const savedItem = savedMap.get(cardId);
        const cardConfig = CARD_REGISTRY[cardId];
        const minW = cardConfig?.minWidth || 2;
        const minH = cardConfig?.minHeight || 2;

        // Pinned rows always use computed positions (summary, capture, and info trio).
        if (
          savedItem &&
          !usesPinnedLayout(cardId) &&
          savedItem.x !== undefined &&
          savedItem.y !== undefined &&
          savedItem.x >= 0 &&
          savedItem.y >= 0
        ) {
          const defaultItem = defaultLayout.find((d) => d.i === cardId);
          // CRITICAL: Always use CARD_REGISTRY as the source of truth for default dimensions
          // This ensures cards always have valid dimensions even if saved layouts are corrupted
          const defaultW = cardConfig?.defaultWidth || defaultItem?.w || 4;
          const defaultH = cardConfig?.defaultHeight || defaultItem?.h || 4;

          // CRITICAL: If saved dimensions are invalid (0, undefined, null, or too small), use defaults from CARD_REGISTRY
          // This prevents cards from showing as 1x1 after reset or when switching views
          // Only use saved dimensions if they're valid numbers and meet minimum requirements
          // Reject any saved dimensions that are less than minimum (including 1x1 for cards with minW/minH >= 2)
          const savedW =
            savedItem.w &&
            typeof savedItem.w === 'number' &&
            savedItem.w >= minW
              ? savedItem.w
              : defaultW;
          const savedH =
            savedItem.h &&
            typeof savedItem.h === 'number' &&
            savedItem.h >= minH
              ? savedItem.h
              : defaultH;
          // Ensure dimensions meet minimum requirements (double-check)
          const w = Math.max(savedW, minW);
          const h = Math.max(savedH, minH);
          // CRITICAL: Clamp minW to never exceed w - this prevents prop validation errors
          // when responsive widths are smaller than the card's minWidth for smaller breakpoints
          const finalMinW = Math.min(savedItem.minW || minW, w);

          if (defaultItem && savedPositionOverlapsPinnedRows(savedItem.y)) {
            const defaultMinW = Math.min(minW, w);
            return {
              i: cardId,
              x: defaultItem.x,
              y: defaultItem.y,
              w: Math.max(defaultItem.w || defaultW, minW),
              h: Math.max(defaultItem.h || defaultH, minH),
              minW: defaultMinW,
              minH: savedItem.minH || minH,
            };
          }

          const result = {
            i: cardId,
            x: savedItem.x,
            y: savedItem.y,
            w: w,
            h: h,
            minW: finalMinW,
            minH: savedItem.minH || minH,
          };
          if (cardId === 'calendar') {
            // CRITICAL: Validate calendar position - it should NEVER be at (0,0) unless it's actually first in default order
            // Calendar is at index 7 in default order for 'my' view, so (0,0) is wrong
            const isAtFirstPosition = savedItem.x === 0 && savedItem.y === 0;
            const shouldBeAtFirstPosition =
              defaultItem && defaultItem.x === 0 && defaultItem.y === 0;
            const positionIsWrong =
              isAtFirstPosition && !shouldBeAtFirstPosition;

            // If saved position is (0,0) but default says it shouldn't be, use default position instead
            if (positionIsWrong && defaultItem) {
              if (breakpoint === 'lg') {
                console.warn(
                  `[CALENDAR DEBUG] mergeLayouts (${breakpoint}): Calendar saved position is wrong (0,0) - using default position`,
                  {
                    savedPosition: { x: savedItem.x, y: savedItem.y },
                    defaultPosition: { x: defaultItem.x, y: defaultItem.y },
                  }
                );
              }
              // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
              const finalMinW = Math.min(savedItem.minW || minW, w);
              return {
                i: cardId,
                x: defaultItem.x, // Use default x position
                y: defaultItem.y, // Use default y position
                w: w,
                h: h,
                minW: finalMinW,
                minH: savedItem.minH || minH,
              };
            }
          }
          return result;
        }
        // No saved position for this card - use system default from the current view
        // This only happens for newly added cards or cards that don't have saved layouts
        // System defaults are view-specific (My View vs Manager View have different defaults)
        // The defaultLayout is generated by getDefaultLayouts() which uses dashboardViewType
        const defaultItem = defaultLayout.find((d) => d.i === cardId);
        // CRITICAL: Always use CARD_REGISTRY as the source of truth for default dimensions
        // This ensures cards always have valid dimensions even if defaultLayout is missing dimensions
        const defaultW = cardConfig?.defaultWidth || defaultItem?.w || 4;
        const defaultH = cardConfig?.defaultHeight || defaultItem?.h || 4;

        if (defaultItem) {
          // Use the system default position and size for this view
          // CRITICAL: Use CARD_REGISTRY dimensions as fallback to ensure valid dimensions
          const w = Math.max(defaultItem.w || defaultW, minW);
          const h = Math.max(defaultItem.h || defaultH, minH);
          // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
          const finalMinW = Math.min(minW, w);
          const result = {
            i: cardId,
            x: defaultItem.x,
            y: defaultItem.y,
            w: w,
            h: h,
            minW: finalMinW,
            minH: minH,
          };
          return result;
        }
        // Fallback (should not happen if card is in cardsForLayout)
        // CRITICAL: Use CARD_REGISTRY dimensions as fallback
        const w = Math.max(defaultW, minW);
        const h = Math.max(defaultH, minH);
        // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
        const finalMinW = Math.min(minW, w);
        return {
          i: cardId,
          x: 0,
          y: 0,
          w: w,
          h: h,
          minW: finalMinW,
          minH: minH,
        };
      });

      // Log the merged result for calendar
      if (breakpoint === 'lg') {
        const calendarInMerged = merged.find(
          (item: any) => item.i === 'calendar'
        );
      }

      return merged;
    };

    const finalLayouts = {
      xl: mergeLayouts(layoutsToMerge?.xl, defaultLayouts.xl, 'xl'),
      lg: mergeLayouts(layoutsToMerge?.lg, defaultLayouts.lg, 'lg'),
      md: mergeLayouts(layoutsToMerge?.md, defaultLayouts.md, 'md'),
      sm: mergeLayouts(layoutsToMerge?.sm, defaultLayouts.sm, 'sm'),
      xs: mergeLayouts(layoutsToMerge?.xs, defaultLayouts.xs, 'xs'),
      xxs: mergeLayouts(layoutsToMerge?.xxs, defaultLayouts.xxs, 'xxs'),
    };
    // CRITICAL: Ensure ALL breakpoints are arrays (not undefined)
    // ResponsiveGridLayout requires all breakpoints to be arrays
    const allBreakpoints: Array<'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'> = [
      'xl',
      'lg',
      'md',
      'sm',
      'xs',
      'xxs',
    ];
    allBreakpoints.forEach((bp) => {
      if (!finalLayouts[bp] || !Array.isArray(finalLayouts[bp])) {
        // Use lg as fallback if breakpoint is missing or not an array
        finalLayouts[bp] = finalLayouts.lg || defaultLayouts[bp] || [];
      }
    });

    // Final safety pass: ensure grid items respect breakpoint column limits.
    // react-grid-layout will warn if minW is larger than the item's width/maxWidth (i.e. column count).
    // Clamp each item's w and minW so that: 1 <= minW <= w <= cols[bp].
    const normalizedLayouts: {
      xl?: any[];
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    } = { ...finalLayouts };

    allBreakpoints.forEach((bp) => {
      const bpCols = cols[bp];
      if (!normalizedLayouts[bp] || !Array.isArray(normalizedLayouts[bp])) {
        return;
      }
      normalizedLayouts[bp] = normalizedLayouts[bp]!.map((item: any) => {
        // Default to at least 1 column if cols is misconfigured
        const maxCols = Math.max(1, bpCols || 1);
        let w = typeof item.w === 'number' ? item.w : 1;
        // Clamp width into [1, maxCols]
        w = Math.min(Math.max(w, 1), maxCols);

        let minW =
          typeof item.minW === 'number' && item.minW > 0 ? item.minW : 1;
        // Ensure minW never exceeds w or maxCols
        minW = Math.min(minW, w, maxCols);

        return {
          ...item,
          w,
          minW,
        };
      });
    });

    // CRITICAL: Store final layouts in currentLayoutRef for use when removing cards
    // This ensures we always have current positions even if pendingLayouts is empty
    currentLayoutRef.current = normalizedLayouts;
    const calendarInFinal = normalizedLayouts.lg?.find(
      (item: any) => item.i === 'calendar'
    );
    const calendarInLayoutsToMerge = layoutsToMerge?.lg?.find(
      (item: any) => item.i === 'calendar'
    );
    return normalizedLayouts;
  }, [
    pwaMobileNav,
    isEditMode,
    pendingLayouts,
    cardsForLayout,
    getDefaultLayouts,
    savedLayouts,
    cardLayouts,
    pendingReset,
    justSaved,
    justSavedReset,
    cols,
  ]);

  // For backward compatibility - use desktop layout
  const initialLayout = React.useMemo(() => {
    const cardIds = cardsForLayout.map((c) => c);

    // Use cardLayouts from preferences if available, otherwise use savedLayout
    const layoutToUse = cardLayouts?.lg || cardLayouts?.xl || savedLayout;

    // Always merge saved layouts with defaults to preserve positions
    // This ensures cards like Calendar and Returns by Due Date keep their positions
    // even when the card set changes or screen updates occur
    const defaultLayout = getDefaultLayout(cardsForLayout);
    const savedMap = new Map((layoutToUse || []).map((l: any) => [l.i, l]));

    const newLayout = cardsForLayout.map((cardId) => {
      const saved = savedMap.get(cardId);
      const defaultItem = defaultLayout.find((d) => d.id === cardId);
      const cardConfig = CARD_REGISTRY[cardId];
      const minW = cardConfig?.minWidth || 2;
      const minH = cardConfig?.minHeight || 2;

      if (
        saved &&
        !usesPinnedLayout(cardId) &&
        saved.x !== undefined &&
        saved.y !== undefined &&
        saved.x >= 0 &&
        saved.y >= 0
      ) {
        const w = Math.max(saved.w || defaultItem?.w || 4, minW);
        const h = Math.max(saved.h || defaultItem?.h || 4, minH);
        const finalMinW = Math.min(saved.minW || minW, w);

        if (defaultItem && savedPositionOverlapsPinnedRows(saved.y)) {
          return {
            i: cardId,
            x: defaultItem.x,
            y: defaultItem.y,
            w: Math.max(defaultItem.w || 4, minW),
            h: Math.max(defaultItem.h || 4, minH),
            minW: Math.min(minW, w),
            minH: saved.minH || minH,
          };
        }

        return {
          i: cardId,
          x: saved.x,
          y: saved.y,
          w: w,
          h: h,
          minW: finalMinW,
          minH: saved.minH || minH,
        };
      }

      // Use default position from the full layout calculation (not single card)
      // This ensures cards are positioned correctly relative to each other
      if (defaultItem) {
        const w = Math.max(defaultItem.w || 4, minW);
        const h = Math.max(defaultItem.h || 4, minH);
        // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
        const finalMinW = Math.min(minW, w);
        return {
          i: cardId,
          x: defaultItem.x,
          y: defaultItem.y,
          w: w,
          h: h,
          minW: finalMinW,
          minH: minH,
        };
      }

      // Fallback (should not happen)
      const w = 4;
      const h = 4;
      // CRITICAL: Clamp minW to never exceed w - prevents prop validation errors
      const finalMinW = Math.min(minW, w);
      return {
        i: cardId,
        x: 0,
        y: 0,
        w: w,
        h: h,
        minW: finalMinW,
        minH: minH,
      };
    });
    return newLayout;
  }, [cardsForLayout, getDefaultLayout, savedLayout, cardLayouts]);

  // Always use initialLayout which merges saved positions with defaults
  // This ensures cards like Calendar and Returns by Due Date keep their positions
  // even when the card set changes or screen updates occur
  // The initialLayout function already handles merging saved positions with defaults
  const layout = initialLayout;

  // Handle layout change for responsive layouts
  const handleLayoutChange = React.useCallback(
    (newLayout: any[], allLayouts?: { [key: string]: any[] }) => {
      // If we just saved (regular or reset), ignore layout changes to prevent overwriting
      // The layout should remain stable until the state fully propagates
      if (justSaved || justSavedReset) {
        return;
      }

      // CRITICAL: When not in edit mode, GridLayout may call onLayoutChange during
      // internal recalculations. We should NOT save these changes as they can cause
      // cards to jump to wrong positions and then persist those wrong positions.
      // Only save layouts when user explicitly moves/resizes in edit mode.
      if (!isEditMode) {
        // Don't save - just return to prevent overwriting saved positions
        return;
      }

      // In edit mode, update pending state immediately so the card stays in its new position
      if (isEditMode) {
        if (allLayouts) {
          // CRITICAL: Ensure ALL breakpoints are present (xl, lg, md, sm, xs, xxs)
          // If a breakpoint is missing, use lg as fallback (most common)
          const allBreakpoints: Array<
            'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
          > = ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'];
          const completeLayouts: {
            xl?: any[];
            lg?: any[];
            md?: any[];
            sm?: any[];
            xs?: any[];
            xxs?: any[];
          } = { ...allLayouts };
          allBreakpoints.forEach((bp) => {
            if (!completeLayouts[bp] && completeLayouts.lg) {
              // Use lg as fallback for missing breakpoints
              completeLayouts[bp] = completeLayouts.lg;
            }
          });
          setPendingLayouts(completeLayouts);
          setPendingLayout(
            completeLayouts.xl || completeLayouts.lg || newLayout
          );
          // CRITICAL: Also store in ref for use when removing cards
          currentLayoutRef.current = completeLayouts;
        } else {
          // If allLayouts is not provided, update pendingLayout and also update pendingLayouts
          // by using the current layout for all breakpoints
          setPendingLayout(newLayout);
          setPendingLayouts((prev) => {
            // Preserve existing breakpoints but update with new layout
            const updated = { ...prev };
            updated.xl = newLayout;
            updated.lg = newLayout;
            updated.md = newLayout;
            updated.sm = newLayout;
            updated.xs = newLayout;
            updated.xxs = newLayout;
            return updated;
          });
        }

        // Extract heights and widths from layout and store in pending state
        const heights: Record<string, number> = {};
        const widths: Record<string, number> = {};
        newLayout.forEach((item) => {
          if (item.i) {
            if (item.h) {
              heights[item.i] = item.h;
            }
            if (item.w) {
              widths[item.i] = item.w;
            }
          }
        });
        if (Object.keys(heights).length > 0) {
          setPendingHeights(heights);
        }
        if (Object.keys(widths).length > 0) {
          setPendingWidths(widths);
        }
        return;
      }

      // Not in edit mode - DO NOT save layouts from onLayoutChange
      // GridLayout calls onLayoutChange during internal recalculations,
      // which can cause cards to jump to wrong positions and then save those wrong positions
      // Only save layouts when user explicitly moves/resizes in edit mode
      // The saved layouts from preferences (cardLayouts prop) should be the source of truth
      if (!isEditMode) {
        // Don't save - just return to prevent overwriting saved positions
        return;
      }

      // Get new order from visible cards (sorted by position)
      const newVisibleOrder = newLayout
        .sort((a, b) => {
          if (a.y !== b.y) return a.y - b.y;
          return a.x - b.x;
        })
        .map((item) => item.i as CardId);

      // Get current full card order (including filtered cards)
      const currentDefaultOrder = getDefaultCardOrder();
      const currentFullOrder: CardId[] =
        cardOrder.length > 0 ? (cardOrder as CardId[]) : currentDefaultOrder;

      // Create a set of visible card IDs for quick lookup
      const visibleCardSet = new Set(cardsForLayout);

      // Extract filtered cards from current order (preserving their relative order)
      const filteredCards = currentFullOrder.filter(
        (cardId) => !visibleCardSet.has(cardId)
      );

      // Update pending order (only visible cards, filtered cards will be preserved on save)
      setPendingCardOrder(newVisibleOrder);
    },
    [
      justSaved,
      justSavedReset,
      isEditMode,
      cardOrder,
      cardsForLayout,
      getDefaultCardOrder,
      saveCardHeights,
      saveCardWidths,
    ]
  );

  // Render skeleton for a card based on its type
  const renderCardSkeleton = (cardId: CardId) => {
    const cardConfig = CARD_REGISTRY[cardId];
    if (!cardConfig) return null;

    // Different skeleton layouts for different card types
    switch (cardId) {
      case 'totalEntities':
      case 'totalReturns':
      case 'notStarted':
      case 'pendingTasks':
      case 'awaitingReview':
      case 'submittedThisMonth':
      case 'capturePendingReview':
      case 'captureReadyToPost':
      case 'capturePostedFiles':
      case 'captureFilesUploaded':
        // Simple stat cards - show title and number skeleton
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton.Input
                active
                size="large"
                style={{ width: '80px', height: '48px' }}
              />
            </div>
          </BaseCard>
        );
      case 'tasksByStatus':
        // Pie chart card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div
              style={{
                marginTop: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Skeleton.Avatar active size={200} shape="circle" />
            </div>
          </BaseCard>
        );
      case 'returnsByDueDate':
        // Table card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 5 }} />
            </div>
          </BaseCard>
        );
      case 'typesOfReturns':
      case 'yourRoleInReturns':
        // Horizontal bar chart cards
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </BaseCard>
        );
      case 'teamAllocation':
        // Large table card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          </BaseCard>
        );
      case 'calendar':
        // Calendar card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          </BaseCard>
        );
      case 'submittedReturnsChart':
        // Chart and table card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 3 }} />
              <div style={{ marginTop: '24px' }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </div>
            </div>
          </BaseCard>
        );
      case 'timeToSubmit':
        // Table card
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: '60%' }} />
            <div style={{ marginTop: '24px' }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          </BaseCard>
        );
      default:
        return (
          <BaseCard cardId={cardId}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </BaseCard>
        );
    }
  };

  // Render function for each card type using CardRenderer
  const renderCard = (cardId: CardId) => {
    // Show skeleton if loading, but check if specific card data is available first
    // Some cards (like timeToSubmit and submittedReturnsChart) depend on VAT logs data
    // which loads independently from organisation data
    let shouldShowSkeleton = isLoading;

    if (isLoading) {
      // For timeToSubmit card, check if metrics data is available
      if (
        cardId === 'timeToSubmit' &&
        timeToSubmitMetrics &&
        timeToSubmitMetrics.length > 0
      ) {
        shouldShowSkeleton = false;
      }
      // For submittedReturnsChart card, check if chart data is available
      if (
        cardId === 'submittedReturnsChart' &&
        submittedReturnsData &&
        submittedReturnsData.length > 0 &&
        filteredSubmittedReturns &&
        filteredSubmittedReturns.length > 0
      ) {
        shouldShowSkeleton = false;
      }
    }

    if (shouldShowSkeleton) {
      return renderCardSkeleton(cardId);
    }

    // Build props object for each card type
    const baseProps = {
      dashboardViewType,
      currentUser,
      cardId, // Pass cardId so cards can get title from registry
    };

    switch (cardId) {
      case 'totalEntities':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              entities,
              onClick: handleTotalEntitiesClick,
            }}
          />
        );
      case 'totalReturns':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              mappedReturns: getAllReturns,
              onClick: handleTotalReturnsClick,
            }}
          />
        );
      case 'notStarted':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              notStartedCount,
              onClick: handleNotStartedClick,
            }}
          />
        );
      case 'pendingTasks':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              pendingTasks,
              onClick: handlePendingTasksClick,
            }}
          />
        );
      case 'awaitingReview':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              awaitingReview,
              onClick: handleAwaitingReviewClick,
            }}
          />
        );
      case 'submittedThisMonth':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              submittedThisMonth: submittedThisMonthValue,
              onClick: handleSubmittedThisMonthClick,
            }}
          />
        );
      case 'capturePendingReview':
      case 'captureReadyToPost':
      case 'capturePostedFiles':
      case 'captureFilesUploaded':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              captureDashboardStats,
              onClick: handleTotalEntitiesClick,
            }}
          />
        );
      case 'tasksByStatus':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              savedTasks,
              awaitingClientTasks,
              reviewerApprovedTasks,
              awaitingApprovalTasks,
              approvedTasks,
              clientApprovedTasks,
              lockedTasks,
              submittedTasks,
              cancelledTasks,
              notSavedTasks,
              exceptionsModifiedTasks,
              notStartedTasks,
              handleStatusClick,
              navigateToSubmissions,
              getReturnsByStatus,
            }}
          />
        );
      case 'returnsByDueDate':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              overdueReturns,
              dueTodayReturns,
              dueThisWeekReturns,
              dueThisMonthReturns,
              dueNextMonthReturns,
              dueLaterReturns,
              handleDueDateClick,
              getReturnsByDueDate,
              createSubmission,
            }}
          />
        );
      case 'typesOfReturns':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              returnTypeCounts,
              maxReturnTypeValue,
              handleReturnTypeClick,
              getReturnsByType,
              createSubmission,
            }}
          />
        );
      case 'yourRoleInReturns':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              preparerCount,
              reviewerCount,
              maxRoleValue,
              handleRoleClick,
              getReturnsByRole,
              createSubmission,
            }}
          />
        );
      case 'vatNumberChecker':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              vatCheckerSummary,
            }}
          />
        );
      case 'teamAllocation':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              accountUsers,
              userData,
              filteredUserData,
              selectedUserNames,
              tempSelectedUserNames,
              teamAllocationFilterOpen,
              hoveredTeamAllocation,
              setHoveredTeamAllocation,
              setSelectedUserNames,
              setTempSelectedUserNames,
              setTeamAllocationFilterOpen,
              handlePersonClick,
              getReturnsByPersonAndRole,
              createSubmission,
              findUserByName,
              renderRole,
              renderLastLogin,
              renderAvatar,
              t,
            }}
          />
        );
      case 'calendar':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              calendarValue,
              setCalendarValue,
              calendarMode,
              onCalendarModeChange: saveCalendarMode,
              getReturnsForDate,
              createSubmission,
            }}
          />
        );
      case 'submittedReturnsChart':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              submittedReturnsData,
              filteredSubmittedReturns,
              submittedDateRange,
              submittedDateRangeOption,
              enableCustomRange,
              submittedDateFilterOpen,
              setSubmittedDateRange,
              onSubmittedDateOptionSelect,
              onCustomRangeCheckboxChange,
              handleApplyDateFilter,
              handleCancelDateFilter,
              handleOpenDateFilterDropdown,
              openSubmission,
              capturedSubmissionsPageSize,
              saveCapturedSubmissionsPageSize,
              t,
            }}
          />
        );
      case 'timeToSubmit':
        return (
          <CardRenderer
            cardId={cardId}
            props={{
              ...baseProps,
              timeToSubmitMetrics,
              timeToSubmitDateRange,
              timeToSubmitDateRangeOption,
              enableTimeToSubmitCustomRange,
              timeToSubmitDateFilterOpen,
              setTimeToSubmitDateRange,
              onTimeToSubmitDateOptionSelect,
              onTimeToSubmitCustomRangeCheckboxChange,
              handleApplyTimeToSubmitDateFilter,
              handleCancelTimeToSubmitDateFilter,
              handleOpenTimeToSubmitDateFilterDropdown,
              t,
            }}
          />
        );
      default:
        return null;
    }
  };

  // Responsive padding for smaller screens
  const containerPadding = React.useMemo(() => {
    if (windowWidth < 768) return '20px'; // Mobile: less padding
    if (windowWidth < 1024) return '30px'; // Tablet: medium padding
    return '40px'; // Desktop: full padding
  }, [windowWidth]);

  // Scrollbar detection for container width - matches submissions page
  const [hasScrollbar, setHasScrollbar] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const checkScrollbar = () => {
      clearTimeout(timeoutId);
      // Use setTimeout to ensure DOM is fully rendered
      timeoutId = setTimeout(() => {
        const hasVerticalScrollbar =
          document.documentElement.scrollHeight >
          document.documentElement.clientHeight;
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        setHasScrollbar(hasVerticalScrollbar && scrollbarWidth > 0);
      }, 100);
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    // Do NOT listen to 'scroll' - causes setState on every scroll → re-renders → janky/endless scrolling
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollbar);
    };
  }, []);

  // Render content for each tab - this is the actual dashboard grid content
  // Note: The content will automatically reflect the correct view type via dashboardViewType state
  // When a tab is active, dashboardViewType is set, which causes cardsForLayout and initialLayouts to update
  // Moved here after all dependencies are defined
  const renderTabContent = React.useCallback(() => {
    // Safety check: don't render if cardsForLayout or initialLayouts are not ready
    // Check for undefined first before accessing properties
    // Allow empty arrays (no cards is valid), but ensure it's an array
    if (!cardsForLayout || !Array.isArray(cardsForLayout) || !initialLayouts) {
      return (
        <div
          style={{
            maxWidth: 1600,
            minWidth: 1200,
            margin: 'auto',
            width: '100%',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      );
    }

    return (
      <>
        <DocumentCaptureBanner />
        {pwaMobileNav ? (
          <PwaDashboardGrid cardIds={cardsForLayout} renderCard={renderCard} />
        ) : (
          <>
            {/* Add smooth transitions for grid items */}
            <style>{`
          .react-grid-item {
            transition: transform 0.2s ease-out, width 0.2s ease-out, height 0.2s ease-out !important;
          }
          .react-grid-item.cssTransforms {
            transition: transform 0.2s ease-out !important;
          }
          .react-grid-item > .react-resizable-handle {
            transition: opacity 0.2s ease-out !important;
          }
          .react-grid-item > .react-resizable-handle::after {
            border-right-color: var(--color-text-base, #e3eaeb) !important;
            border-bottom-color: var(--color-text-base, #e3eaeb) !important;
          }
          /* Prevent immediate repositioning during drag - only reposition on drop */
          .react-grid-item.react-grid-placeholder {
            transition: all 0.2s ease-out !important;
          }
        `}</style>
            <ResponsiveGridLayout
              className="dashboard-grid-layout"
              layouts={initialLayouts}
              breakpoints={breakpoints}
              cols={cols}
              rowHeight={60}
              onLayoutChange={handleLayoutChange}
              isDraggable={isEditMode}
              isResizable={isEditMode}
              draggableHandle=".drag-handle"
              compactType="vertical"
              preventCollision={false}
              style={{
                width: '100%',
                minHeight: '400px',
                overflow: 'visible',
                userSelect: isEditMode ? 'none' : 'auto',
                WebkitUserSelect: isEditMode ? 'none' : 'auto',
                MozUserSelect: isEditMode ? 'none' : 'auto',
                msUserSelect: isEditMode ? 'none' : 'text',
              }}
              useCSSTransforms={true}
              margin={[16, 16]}
            >
              {cardsForLayout.map((cardId: CardId) => {
                const cardElement = renderCard(cardId);
                if (!cardElement) return null;

                return (
                  <div
                    key={cardId}
                    style={{
                      cursor: isEditMode ? 'move' : 'default',
                      height: '100%',
                      userSelect: isEditMode ? 'none' : 'auto',
                      WebkitUserSelect: isEditMode ? 'none' : 'auto',
                      MozUserSelect: isEditMode ? 'none' : 'auto',
                      msUserSelect: isEditMode ? 'none' : 'text',
                    }}
                    onMouseDown={(e) => {
                      if (isEditMode) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {isEditMode && (
                      <div
                        className="drag-handle"
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '20px',
                          height: '20px',
                          cursor: 'move',
                          zIndex: 1000,
                          background: 'rgba(0,0,0,0.1)',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '12px',
                            color: 'var(--color-text-base, #e3eaeb)',
                          }}
                        >
                          ⋮⋮
                        </span>
                      </div>
                    )}
                    {cardElement}
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          </>
        )}
      </>
    );
  }, [
    initialLayouts,
    breakpoints,
    cols,
    handleLayoutChange,
    isEditMode,
    pwaMobileNav,
    cardsForLayout,
    renderCard,
  ]);

  // Memoize tab items to prevent flickering (My View first)
  // Now each tab contains its own content
  // Note: The content will automatically update when dashboardViewType changes via handleTabChange
  // This is because cardsForLayout and initialLayouts depend on dashboardViewType
  // Moved here after renderTabContent is defined
  const tabItems = React.useMemo(() => {
    const items = [
      {
        key: 'my',
        label: (
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            {t('dashboard.my-view')}
          </span>
        ),
        children: renderTabContent(),
      },
    ];

    // Hide Manager View unless the user has managerViewEnabled.
    if (!isManager) {
      return items;
    }

    items.push({
      key: 'manager',
      label: !hasManagerViewAccess ? (
        <StyledTooltip // @ts-ignore
          title={t('dashboard.unlock-manager-view-tooltip')}
          placement="bottom"
          arrow={false}
        >
          <Space style={{ cursor: 'pointer' }}>
            <TeamOutlined style={{ marginRight: 8 }} />
            {t('dashboard.manager-view')}
            <LockFilled
              style={{
                color: 'var(--color-text-muted, #5a6a6a)',
                fontSize: '12px',
              }}
            />
          </Space>
        </StyledTooltip>
      ) : (
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          {t('dashboard.manager-view')}
        </span>
      ),
      children: renderTabContent(),
    });

    return items;
  }, [
    hasManagerViewAccess,
    handleUpgradeClick,
    renderTabContent,
    dashboardViewType,
    isManager,
    t,
    pwaMobileNav,
  ]);

  return (
    <div
      className={pwaMobileNav ? 'scope-pwa-dashboard' : undefined}
      style={{
        ...(pwaMobileNav
          ? {}
          : {
              padding: '40px',
              paddingTop: '10px',
            }),
        maxWidth: pwaMobileNav ? '100%' : hasScrollbar ? 1662 : 1678,
        minWidth: pwaMobileNav ? 0 : 1200,
        margin: 'auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {pwaMobileNav && (
        <Typography.Title level={4} className="scope-pwa-page-title">
          {t('navigation.dashboard')}
        </Typography.Title>
      )}
      <Row
        style={{
          marginTop: -5,
          marginBottom: 20,
          position: 'relative',
        }}
      >
        <ViewTabsContainer
          className={
            pwaMobileNav
              ? 'scope-dashboard-view-tabs scope-dashboard-view-tabs--split'
              : undefined
          }
        >
          <ScopeTabs
            activeKey={dashboardViewType}
            onChange={handleTabChange}
            items={tabItems}
            animated={false}
            destroyOnHidden
          />
        </ViewTabsContainer>
        <div
          style={{
            position: 'absolute',
            insetInlineEnd: 0,
            top: 0,
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          {!isEditMode && !pwaMobileNav ? (
            <StyledTooltip // @ts-ignore
              title={
                !hasManagerViewAccess
                  ? t('dashboard.unlock-manager-view-tooltip')
                  : null
              }
              placement="bottom"
              arrow={false}
            >
              <ExportButton
                className="dashboard-edit-view-button"
                icon={
                  <EditOutlined
                    style={{
                      fontSize: 14,
                      color: 'var(--color-text-base, #2e2e2e)',
                    }}
                  />
                }
                onClick={
                  hasManagerViewAccess ? handleEditClick : handleUpgradeClick
                }
                style={{
                  height: 30,
                  padding: '0 8px',
                  margin: '7px 0px 7px 13px',
                  cursor: hasManagerViewAccess ? 'pointer' : 'not-allowed',
                  opacity: hasManagerViewAccess ? 1 : 0.6,
                }}
              >
                <Space size={4}>
                  {t('dashboard.edit-view')}
                  {!hasManagerViewAccess && (
                    <LockFilled
                      style={{
                        color: 'var(--color-text-muted, #5a6a6a)',
                        fontSize: '11px',
                      }}
                    />
                  )}
                </Space>
              </ExportButton>
            </StyledTooltip>
          ) : !pwaMobileNav ? (
            <Space>
              <ScopeButton
                type="default"
                width="100px"
                height="30px"
                fontSize="12px"
                lineheight="12px"
                style={{
                  padding: '4px 5px',
                  margin: '7px 0px 7px 13px',
                }}
                onClick={() => {
                  const drawerData: DrawerData = {
                    title: t('dashboard.customize-cards'),
                    subtitle:
                      dashboardViewType === 'manager'
                        ? t('dashboard.manager-view')
                        : t('dashboard.my-view'),
                    type: DrawerType.CUSTOMIZE_DASHBOARD_CARDS,
                    component: (
                      <CardSelector
                        visibleCards={
                          isEditMode
                            ? pendingCardOrder.length > 0
                              ? pendingCardOrder
                              : filteredVisibleCards
                            : filteredVisibleCards
                        }
                        onSave={(newVisibleCards) => {
                          // CRITICAL: In edit mode, update pendingLayouts immediately for preview
                          // This ensures added cards show with correct dimensions in edit preview
                          if (isEditMode) {
                            // Find cards that were added
                            const addedCards = (
                              newVisibleCards as CardId[]
                            ).filter((id) => !pendingCardOrder.includes(id));

                            if (addedCards.length > 0) {
                              // Lay out newly added cards at the top (order already
                              // has newest first from CardSelector), then shift existing cards down.
                              const addedInOrder = (
                                newVisibleCards as CardId[]
                              ).filter((id) => addedCards.includes(id));
                              const addedLayoutsByBp =
                                getDefaultLayouts(addedInOrder);

                              const mergedLayouts: typeof pendingLayouts = {
                                ...pendingLayouts,
                              };
                              const allBreakpoints: Array<
                                'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
                              > = ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'];

                              allBreakpoints.forEach((bp) => {
                                const existingMap = new Map(
                                  (mergedLayouts[bp] || []).map((item: any) => [
                                    item.i,
                                    item,
                                  ])
                                );
                                const addedLayoutItems =
                                  addedLayoutsByBp[bp] || [];
                                const addedMap = new Map(
                                  addedLayoutItems.map((item: any) => [
                                    item.i,
                                    item,
                                  ])
                                );
                                const shiftY = addedLayoutItems.reduce(
                                  (max: number, item: any) =>
                                    Math.max(
                                      max,
                                      (item.y || 0) + (item.h || 0)
                                    ),
                                  0
                                );

                                const merged = newVisibleCards
                                  .map((cardId) => {
                                    const addedItem = addedMap.get(cardId);
                                    if (addedItem) {
                                      const cardConfig =
                                        CARD_REGISTRY[cardId as CardId];
                                      const minW = cardConfig?.minWidth || 2;
                                      const minH = cardConfig?.minHeight || 2;
                                      const defaultW =
                                        cardConfig?.defaultWidth || 4;
                                      const defaultH =
                                        cardConfig?.defaultHeight || 4;
                                      const w = Math.max(
                                        addedItem.w || defaultW,
                                        minW
                                      );
                                      const h = Math.max(
                                        addedItem.h || defaultH,
                                        minH
                                      );
                                      return {
                                        ...addedItem,
                                        w,
                                        h,
                                        minW: Math.min(minW, w),
                                        minH,
                                      };
                                    }

                                    const existing = existingMap.get(cardId);
                                    if (existing) {
                                      return {
                                        ...existing,
                                        y: (existing.y || 0) + shiftY,
                                      };
                                    }
                                    return null;
                                  })
                                  .filter(Boolean);

                                mergedLayouts[bp] = merged;
                              });

                              setPendingLayouts(mergedLayouts);
                              setPendingLayout(
                                mergedLayouts.lg || mergedLayouts.xl || []
                              );
                              setPendingCardOrder(newVisibleCards as CardId[]);
                            } else {
                              // No cards added, just update order
                              setPendingCardOrder(newVisibleCards as CardId[]);
                            }
                          }

                          // CRITICAL: Pass current layouts to preserve positions when cards are removed
                          // This prevents cards from moving when cardLayouts is null
                          // IMPORTANT: Use pendingLayouts if available, otherwise use currentLayoutRef
                          // This ensures we always have current positions even if user hasn't moved cards
                          // Check if pendingLayouts has actual layout arrays (not just empty object)
                          const hasPendingLayouts =
                            pendingLayouts &&
                            ((pendingLayouts.xl &&
                              pendingLayouts.xl.length > 0) ||
                              (pendingLayouts.lg &&
                                pendingLayouts.lg.length > 0) ||
                              (pendingLayouts.md &&
                                pendingLayouts.md.length > 0) ||
                              (pendingLayouts.sm &&
                                pendingLayouts.sm.length > 0) ||
                              (pendingLayouts.xs &&
                                pendingLayouts.xs.length > 0) ||
                              (pendingLayouts.xxs &&
                                pendingLayouts.xxs.length > 0));
                          // Check if currentLayoutRef has actual layout arrays (not just empty object)
                          const hasCurrentLayoutRef =
                            currentLayoutRef.current &&
                            ((currentLayoutRef.current.xl &&
                              currentLayoutRef.current.xl.length > 0) ||
                              (currentLayoutRef.current.lg &&
                                currentLayoutRef.current.lg.length > 0) ||
                              (currentLayoutRef.current.md &&
                                currentLayoutRef.current.md.length > 0) ||
                              (currentLayoutRef.current.sm &&
                                currentLayoutRef.current.sm.length > 0) ||
                              (currentLayoutRef.current.xs &&
                                currentLayoutRef.current.xs.length > 0) ||
                              (currentLayoutRef.current.xxs &&
                                currentLayoutRef.current.xxs.length > 0));
                          const currentPendingLayouts = hasPendingLayouts
                            ? pendingLayouts
                            : hasCurrentLayoutRef
                            ? currentLayoutRef.current
                            : null;
                          // Pass current layouts to preserve positions when cards are removed
                          // Only save to preferences if not in edit mode (will be saved when Save is clicked)
                          if (!isEditMode) {
                            saveCardVisibilityBatch(
                              newVisibleCards,
                              currentPendingLayouts || undefined
                            );
                          }
                        }}
                        dashboardViewType={dashboardViewType}
                        onClose={() => dispatch(turnDrawerOff())}
                      />
                    ),
                  };
                  dispatch(turnDrawerOn(drawerData));
                }}
              >
                <Space>
                  <PlusOutlined />
                  {t('dashboard.add-card')}
                </Space>
              </ScopeButton>
              <ScopeButton
                type="primary"
                width="85px"
                height="30px"
                fontSize="12px"
                lineheight="12px"
                style={{
                  padding: '4px 5px',
                  margin: '7px 0px 7px 13px',
                }}
                onClick={handleSaveClick}
              >
                <Space>
                  <SaveOutlined />
                  {t('common.save')}
                </Space>
              </ScopeButton>
              <StyledPopconfirm
                title={t('dashboard.reset-title')}
                description={t('dashboard.reset-description')}
                onConfirm={handleResetClick}
                okText={t('common.reset')}
                cancelText={t('common.cancel')}
                placement="topRight"
                overlayStyle={{ width: '400px', maxWidth: '400px' }}
              >
                <ScopeButton
                  type="default"
                  width="110px"
                  height="30px"
                  fontSize="12px"
                  lineheight="12px"
                  style={{
                    padding: '4px 5px',
                    margin: '7px 0px 7px 13px',
                  }}
                >
                  {t('dashboard.reset-layout')}
                </ScopeButton>
              </StyledPopconfirm>
              <ScopeButton
                type="default"
                width="85px"
                height="30px"
                fontSize="12px"
                lineheight="12px"
                onClick={handleCancelClick}
                style={{
                  padding: '4px 5px',
                  margin: '7px 0px 7px 13px',
                }}
              >
                {t('common.cancel')}
              </ScopeButton>
            </Space>
          ) : null}
        </div>
      </Row>
    </div>
  );
};
